import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ExcelImportService } from '../services/excel-import.service';
import { BrandService } from '../services/brand.service';
import { CountryService } from '../services/country.service';
import { MachinesService } from '../services/photolavie/machines.service';
import { StoreService } from '../services/store.service';
import { InvoiceService } from '../services/invoice.service';
import { Firestore, writeBatch, collection, doc, getDocs, query, where, orderBy, limit, collectionGroup, setDoc, increment, serverTimestamp, Timestamp } from '@angular/fire/firestore';
import { initializeApp } from 'firebase/app';
import { getFirestore as getSdkFirestore } from 'firebase/firestore';
import { environment } from '../../environments/environment';
import { ExchangeRateService } from '../services/exchange-rate.service';
import * as XLSX from 'xlsx';
import { firstValueFrom } from 'rxjs';
import { Storage, ref, listAll, deleteObject } from '@angular/fire/storage';
import { Auth } from '@angular/fire/auth';
import { SmrsRate } from '../services/fee-rate.service';
import { resolveKonoboxBrand } from '../konobox-management/konobox-brand.util';
import {
  CouponMigrationOutcome,
  CouponMigrationPlan,
  LavieBrandResolution,
  applyCouponBrandMigration,
  isMigrationConfirmed,
  planCouponBrandMigration,
  resolveCanonicalLavieBrand
} from '../services/coupon-migration.util';

@Component({
  selector: 'app-dev-tool',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './dev-tool.component.html',
  styleUrl: './dev-tool.component.css'
})
export class DevToolComponent implements OnInit, OnDestroy {
  private excelService = inject(ExcelImportService);
  private brandService = inject(BrandService);
  private countryService = inject(CountryService);
  private storeService = inject(StoreService);
  private firestore = inject(Firestore);
  private invoiceService = inject(InvoiceService);
  private machinesService = inject(MachinesService);
  private exchangeRateService = inject(ExchangeRateService);
  private storage = inject(Storage);
  private auth = inject(Auth);

  isMapping = false;
  isUpdatingNames = false;
  isUpdatingMachineCountries = false;
  isSyncingStoreMachineCounts = false;
  storeMachineCountSyncResult: string | null = null;
  isRemovingMachineCounts = false;
  removeMachineCountsResult: string | null = null;
  isPopulatingStores = false;
  isDeletingStores = false;
  isUpdatingBrand = false;
  isUpdatingSmrsRatesBrand = false;
  isMigratingBrands = false;
  isRemappingBrands = false;
  isReassigningCompanyBrandIds = false;
  isMigratingMM = false;
  migrationPeriodMM = '';
  isPreviewingMM = false;
  hasPreviewedMM = false;
  migrationPreviewMM: any[] = [];

  resetSmrsStartPeriod = '';
  resetSmrsEndPeriod = '';
  resetSmrsCompanyId = '';
  isResettingSmrs = false;

  // SMRS Invoice Ledger Integrity Recalculation & Migration (Company-by-Company + Loop)
  isScanningLedger = false;
  isRecalculatingLedger = false;
  isBatchScanning = false;
  isBatchMigrating = false;
  batchProgressText = '';
  batchProgressPercent = 0;
  companiesWithDiscrepancies: Array<{
    companyId: string;
    companyName: string;
    earliestPeriod: string;
    discrepancyCount: number;
    missingCount: number;
    totalPeriods: number;
  }> = [];

  ledgerMigrationBrandId = '';
  ledgerMigrationCompanyId = '';
  hasPreviewedLedger = false;
  ledgerAuditRows: any[] = [];
  ledgerMigrationDiffs: any[] = []; // backward-compatibility alias
  ledgerMigrationStats = {
    totalChecked: 0,
    totalPopulated: 0,
    totalDiscrepancies: 0,
    missingInvoicesCount: 0
  };
  ledgerMigrationLogs: string[] = [];

  get selectedLedgerCompany(): any | null {
    if (!this.ledgerMigrationCompanyId) return null;
    return this.companies.find(c => c.id === this.ledgerMigrationCompanyId) || null;
  }

  onLedgerBrandChange() {
    this.ledgerMigrationCompanyId = '';
    this.hasPreviewedLedger = false;
    this.ledgerAuditRows = [];
    this.ledgerMigrationDiffs = [];
    this.companiesWithDiscrepancies = [];
  }

  onLedgerCompanyChange() {
    this.hasPreviewedLedger = false;
    this.ledgerAuditRows = [];
    this.ledgerMigrationDiffs = [];
  }

  isCleaningPPImages = false;
  ppImagesToDelete: any[] = [];
  isScanningPP = false;

  isFetchingClaimedSessions = false;
  claimedSessions: any[] = [];

  isBackfillingFrames = false;
  backfillLogs: string[] = [];
  backfillPreview: any[] = [];
  hasPreviewedBackfill = false;

  // Device brandId Backfill (AC-2)
  isBackfillingDeviceBrands = false;
  hasPreviewedDeviceBrands = false;
  deviceBrandPreview: { deviceId: string; storeId: string; companyName: string; resolvedBrandId: string; brandName: string }[] = [];
  unresolvedDevices: { deviceId: string; storeId: string; reason: string }[] = [];
  deviceBrandLogs: string[] = [];

  // Master Data Export / Import (Company, Store, Device & Relationships)
  isExportingMasterData = false;
  isImportingMasterData = false;
  masterDataImportData: any = null;
  masterDataImportFileName = '';
  masterDataLogs: string[] = [];
  masterDataImportStats: {
    companies: number;
    stores: number;
    devices: number;
    companyOwnerships: number;
    owners: number;
    brands: number;
  } | null = null;

  async previewBackfillFrameUsage() {
    this.isBackfillingFrames = true;
    this.backfillLogs = ['Starting frame usage backfill PREVIEW...'];
    this.backfillPreview = [];
    this.hasPreviewedBackfill = false;

    const aggregatedUpdates = new Map<string, any>();

    try {
      this.backfillLogs.push('Querying all sessions via collectionGroup...');
      const sessionsQuery = query(collectionGroup(this.firestore, 'sessions'));
      const sessionsSnap = await getDocs(sessionsQuery);

      this.backfillLogs.push(`Found ${sessionsSnap.size} session documents from collectionGroup.`);

      // If 0, let's just do a manual dive on the first device to see what's actually there
      if (sessionsSnap.size === 0) {
        this.backfillLogs.push(`Attempting manual dive into devices to diagnose path...`);
        const devSnap = await getDocs(collection(this.firestore, 'devices'));
        if (devSnap.size > 0) {
          const firstDev = devSnap.docs[0];
          const sSnap = await getDocs(collection(this.firestore, `devices/${firstDev.id}/sessions`));
          this.backfillLogs.push(`Device ${firstDev.id} has ${sSnap.size} docs in 'sessions'`);
          if (sSnap.size > 0) {
            const firstDate = sSnap.docs[0];
            const nestedSnap = await getDocs(collection(this.firestore, `devices/${firstDev.id}/sessions/${firstDate.id}/sessions`));
            this.backfillLogs.push(`Date ${firstDate.id} has ${nestedSnap.size} docs in nested 'sessions'`);
          }
        }
      }

      let processedCount = 0;
      let skippedCount = 0;
      let missingFrameIdCount = 0;

      for (const sessionDoc of sessionsSnap.docs) {
        const data = sessionDoc.data();

        // Let's check if it's the date doc or the actual session doc
        // The date doc has no fields usually, or maybe some. The session doc has FrameId.
        const frameId = data['FrameId'] || data['frameId'] || data['frameid'];
        if (!frameId) {
          missingFrameIdCount++;
          continue;
        }

        // The document path is: devices/{deviceId}/sessions/{dateId}/sessions/{sessionId}
        const pathParts = sessionDoc.ref.path.split('/');
        // Extract deviceId from path: parts[0]="devices", parts[1]=deviceId
        let deviceId = data['DeviceId'];
        if (!deviceId && pathParts.length >= 2 && pathParts[0] === 'devices') {
          deviceId = pathParts[1];
        }
        if (!deviceId) deviceId = 'Unknown';

        const frameGroupId = data['ThemeFrameGroupID'] || data['ThemeFrameGroupId'] || data['themeFrameGroupId'] || data['themeFrameGroupID'] || '';
        const themeId = data['Theme'] || data['theme'] || '';

        let timestamp = data['Timestamp']?.toDate ? data['Timestamp'].toDate() : null;
        if (!timestamp) {
          // Try to extract date from path
          if (pathParts.length >= 4 && pathParts[2] === 'sessions') {
            const dateId = pathParts[3];
            const parts = dateId.split('-');
            if (parts.length >= 2) {
              timestamp = new Date(`${parts[0]}-${parts[1]}-01`);
            }
          }
          if (!timestamp) timestamp = new Date();
        }

        // Only process sessions up to end of August 12, 2026
        const cutoffDate = new Date('2026-08-13T00:00:00');
        if (timestamp >= cutoffDate) {
          skippedCount++;
          continue;
        }

        processedCount++;
        const monthStr = `${timestamp.getFullYear()}-${String(timestamp.getMonth() + 1).padStart(2, '0')}`;
        // Standardize frame id matching
        const cleanFrameId = frameId;
        const fpName = `${cleanFrameId}_FP.png`;

        const key = `${deviceId}_${monthStr}_${cleanFrameId}`;
        if (aggregatedUpdates.has(key)) {
          const existing = aggregatedUpdates.get(key);
          existing.printCount += 1;
        } else {
          aggregatedUpdates.set(key, {
            deviceId,
            monthStr,
            frameId: cleanFrameId,
            fpName,
            frameGroupId,
            themeId,
            printCount: 1,
            path: `devices/${deviceId}/reports/${monthStr}/frames/${cleanFrameId}`
          });
        }
      }

      this.backfillPreview = Array.from(aggregatedUpdates.values());
      this.hasPreviewedBackfill = true;
      this.backfillLogs.push(`Processed ${processedCount} valid sessions.`);
      this.backfillLogs.push(`Skipped ${skippedCount} post-cutoff sessions.`);
      this.backfillLogs.push(`Skipped ${missingFrameIdCount} docs missing FrameId (likely intermediate date docs).`);
      this.backfillLogs.push(`Preview completed! Found ${this.backfillPreview.length} unique frame records to update.`);
    } catch (e) {
      console.error('Error during backfill preview:', e);
      this.backfillLogs.push(`Error: ${e instanceof Error ? e.message : 'Unknown error'}`);
      alert('Error during backfill preview. Check logs for details.');
    } finally {
      this.isBackfillingFrames = false;
    }
  }

  async executeBackfillFrameUsage() {
    if (!this.hasPreviewedBackfill || this.backfillPreview.length === 0) return;
    if (!confirm(`Are you sure you want to write ${this.backfillPreview.length} records to Firestore?`)) return;

    this.isBackfillingFrames = true;
    this.backfillLogs.push('Executing writes to Firestore...');

    try {
      let count = 0;
      // Write in batches of 500
      let currentBatch = writeBatch(this.firestore);
      let batchCount = 0;

      for (const update of this.backfillPreview) {
        const frameReportRef = doc(this.firestore, update.path);
        currentBatch.set(frameReportRef, {
          fpName: update.fpName,
          frameGroupId: update.frameGroupId,
          frameId: update.frameId,
          latestPrintDate: serverTimestamp(),
          printCount: increment(update.printCount),
          themeId: update.themeId,
          tileId: update.themeId
        }, { merge: true });

        count++;
        batchCount++;

        if (batchCount === 500) {
          await currentBatch.commit();
          this.backfillLogs.push(`Committed 500 updates...`);
          currentBatch = writeBatch(this.firestore);
          batchCount = 0;
        }
      }

      if (batchCount > 0) {
        await currentBatch.commit();
      }

      this.backfillLogs.push(`Execution completed successfully! Total records updated: ${count}`);
      this.backfillPreview = [];
      this.hasPreviewedBackfill = false;
    } catch (e) {
      console.error('Error executing backfill:', e);
      this.backfillLogs.push(`Error: ${e instanceof Error ? e.message : 'Unknown error'}`);
      alert('Error during backfill execution. Check logs for details.');
    } finally {
      this.isBackfillingFrames = false;
    }
  }

  async previewBackfillDeviceBrands() {
    this.isBackfillingDeviceBrands = true;
    this.deviceBrandLogs = ['Starting device brandId backfill PREVIEW...'];
    this.deviceBrandPreview = [];
    this.unresolvedDevices = [];
    this.hasPreviewedDeviceBrands = false;

    try {
      // 1. Fetch all brands
      const brandsSnap = await getDocs(collection(this.firestore, 'brands'));
      const brandsMap = new Map<string, any>();
      const brandNameToId = new Map<string, string>();
      brandsSnap.forEach(d => {
        const bData = d.data();
        const bId = d.id;
        brandsMap.set(bId, { id: bId, ...bData });
        const name = (bData['name'] || '').toString().trim().toLowerCase();
        if (name) {
          brandNameToId.set(name, bId);
        }
      });

      // Helper to resolve brand doc ID
      const resolveBrandId = (brandVal: any): string => {
        if (!brandVal) return '';
        const raw = String(brandVal).trim();
        if (brandsMap.has(raw)) return raw;
        const lower = raw.toLowerCase();
        if (brandNameToId.has(lower)) return brandNameToId.get(lower)!;
        if (lower.includes('lavie') || lower.includes('photo') || lower === 'kceg' || raw === 'T0RdUUPStz2b979i3K4A') {
          for (const [n, id] of brandNameToId.entries()) {
            if (n.includes('photolavie') || n.includes('lavie')) return id;
          }
        }
        if (lower === 'lfc' || lower === 'lk' || lower.includes('fourcuts') || lower.includes('4cuts')) {
          for (const [n, id] of brandNameToId.entries()) {
            if (n === 'lfc' || n.includes('fourcuts') || n.includes('4cuts')) return id;
          }
        }
        if (lower.includes('konobox')) {
          for (const [n, id] of brandNameToId.entries()) {
            if (n.includes('konobox')) return id;
          }
        }
        return '';
      };

      // 2. Fetch all companies
      const compSnap = await getDocs(collection(this.firestore, 'company'));
      const compMap = new Map<string, any>();
      compSnap.forEach(d => {
        compMap.set(d.id, d.data());
      });

      // 3. Fetch all stores
      const storeSnap = await getDocs(collection(this.firestore, 'stores'));
      const storeMap = new Map<string, any>();
      storeSnap.forEach(d => {
        const sData = d.data();
        storeMap.set(d.id, sData);
        if (sData['storeId']) storeMap.set(String(sData['storeId']).trim(), sData);
        if (sData['storeCode']) storeMap.set(String(sData['storeCode']).trim(), sData);
      });

      // Find default PhotoLaVie brand ID (no first-brand fallback: that could pick Konobox)
      let photoLavieBrandId = '';
      for (const [id, obj] of brandsMap.entries()) {
        const n = (obj.name || '').toLowerCase();
        if (n.includes('photolavie') || n.includes('lavie') || id === 'T0RdUUPStz2b979i3K4A') {
          photoLavieBrandId = id;
          break;
        }
      }
      // Konobox brand: the exact rule shared by Konobox Management and the CoinSing kiosk
      const konoboxBrandId = resolveKonoboxBrand(brandsSnap.docs.map(d => ({ id: d.id, ...d.data() } as any)))?.id || '';
      const brandNameOf = (id: string) => brandsMap.get(id)?.name || id;

      // 4. Fetch all devices
      const devSnap = await getDocs(collection(this.firestore, 'devices'));
      this.deviceBrandLogs.push(`Found ${devSnap.size} device documents. Scanning for missing brandId...`);

      let alreadyAssignedCount = 0;

      for (const d of devSnap.docs) {
        if (d.id === 'global') continue;
        const devData = d.data();

        // Never overwrite an existing brandId (AC-2)
        if (devData['brandId'] && String(devData['brandId']).trim()) {
          alreadyAssignedCount++;
          continue;
        }

        const deviceId = d.id;
        // Same store lookup as Konobox Management: storeDocId first, then storeId
        const storeDocId = String(devData['storeDocId'] || '').trim();
        const storeCode = String(devData['storeId'] || '').trim();
        const storeId = storeDocId || storeCode;
        const store = (storeDocId && storeMap.get(storeDocId)) || (storeCode && storeMap.get(storeCode)) || null;
        let companyName = 'N/A';
        const companies: any[] = [];

        if (store) {
          const companyIds: string[] = Array.isArray(store['companyIds']) ? store['companyIds'] : [];
          const legacyId = String(store['companyId'] || store['companyDocId'] || store['company'] || '').trim();
          if (legacyId) companyIds.push(legacyId);
          for (const id of companyIds) {
            const company = compMap.get(String(id).trim());
            if (company) companies.push(company);
          }
          if (companies.length > 0) companyName = companies.map(c => c['company'] || '').filter(Boolean).join(', ') || 'N/A';
        }

        // Brand like the management screens: the store's brand decides (Konobox Management lists a device whose store is
        // Konobox, Photobooth Management lists the rest). Store without a brand: its company's brand. Store with no brand
        // at all: PhotoLaVie, as Photobooth Management shows it. No store: unresolved, never guessed.
        let resolvedBrandId = '';
        let reason = '';
        if (!store) {
          reason = storeId ? `Store '${storeId}' not found` : 'No store assigned';
        } else {
          const storeBrandId = String(store['brandId'] || '').trim().toLowerCase() === 'konobox' ? konoboxBrandId : resolveBrandId(store['brandId']);
          const companyBrandIds = [...new Set(companies.map(c => resolveBrandId(c['brand'])).filter(Boolean))];
          if (storeBrandId) resolvedBrandId = storeBrandId;
          else if (companyBrandIds.length === 1) resolvedBrandId = companyBrandIds[0];
          else if (companyBrandIds.length > 1) reason = `Store's companies have different brands (${companyBrandIds.map(brandNameOf).join(', ')})`;
          else resolvedBrandId = photoLavieBrandId;
          if (!resolvedBrandId && !reason) reason = 'PhotoLaVie brand not found in Brands';
        }

        if (!resolvedBrandId) {
          this.unresolvedDevices.push({ deviceId, storeId: storeId || 'N/A', reason });
          continue;
        }
        this.deviceBrandPreview.push({
          deviceId,
          storeId: storeId || 'N/A',
          companyName,
          resolvedBrandId,
          brandName: brandNameOf(resolvedBrandId)
        });
      }

      this.hasPreviewedDeviceBrands = true;
      this.deviceBrandLogs.push(`Scan complete!`);
      this.deviceBrandLogs.push(`• ${alreadyAssignedCount} devices already have brandId assigned (preserved).`);
      this.deviceBrandLogs.push(`• ${this.deviceBrandPreview.length} devices ready to be backfilled.`);
      this.deviceBrandLogs.push(`• ${this.unresolvedDevices.length} unresolved devices (need manual fix).`);

    } catch (err: any) {
      console.error('Error previewing device brand backfill:', err);
      this.deviceBrandLogs.push(`Error: ${err.message || err}`);
      alert('Error during preview. See logs.');
    } finally {
      this.isBackfillingDeviceBrands = false;
    }
  }

  async executeBackfillDeviceBrands() {
    if (!this.hasPreviewedDeviceBrands || this.deviceBrandPreview.length === 0) return;
    if (!confirm(`Are you sure you want to write brandId to ${this.deviceBrandPreview.length} devices in Firestore?`)) return;

    this.isBackfillingDeviceBrands = true;
    this.deviceBrandLogs.push('Executing device brand backfill batch writes...');

    try {
      let batch = writeBatch(this.firestore);
      let batchCount = 0;
      let totalCommitted = 0;

      for (const item of this.deviceBrandPreview) {
        const devRef = doc(this.firestore, 'devices', item.deviceId);
        batch.set(devRef, {
          brandId: item.resolvedBrandId,
          updatedAt: serverTimestamp()
        }, { merge: true });

        batchCount++;
        totalCommitted++;

        if (batchCount >= 450) {
          await batch.commit();
          this.deviceBrandLogs.push(`Committed chunk of ${batchCount} device updates...`);
          batch = writeBatch(this.firestore);
          batchCount = 0;
        }
      }

      if (batchCount > 0) {
        await batch.commit();
        this.deviceBrandLogs.push(`Committed remaining ${batchCount} device updates.`);
      }

      this.deviceBrandLogs.push(`Device brandId backfill completed successfully! Total devices updated: ${totalCommitted}.`);
      alert(`Successfully backfilled brandId for ${totalCommitted} devices.`);
      this.deviceBrandPreview = [];
      this.hasPreviewedDeviceBrands = false;

    } catch (err: any) {
      console.error('Error executing device brand backfill:', err);
      this.deviceBrandLogs.push(`Execution error: ${err.message || err}`);
      alert(`Error during execution: ${err.message || err}`);
    } finally {
      this.isBackfillingDeviceBrands = false;
    }
  }

  async previewReassignAllDeviceBrands() {
    this.isBackfillingDeviceBrands = true;
    this.deviceBrandLogs = ['Starting full device brand REASSIGNMENT PREVIEW (Resetting ALL devices to K-Photo LaVie)...'];
    this.deviceBrandPreview = [];
    this.unresolvedDevices = [];
    this.hasPreviewedDeviceBrands = false;

    try {
      const brandsSnap = await getDocs(collection(this.firestore, 'brands'));
      const brandsMap = new Map<string, any>();
      brandsSnap.forEach(d => {
        const bData = d.data();
        brandsMap.set(d.id, { id: d.id, ...bData });
      });

      let photoLavieBrandId = '';
      let photoLavieBrandName = 'K-Photo LaVie';
      for (const [id, obj] of brandsMap.entries()) {
        const n = (obj.name || '').toLowerCase();
        if (n.includes('photolavie') || n.includes('lavie') || n.includes('photo') || id === 'T0RdUUPStz2b979i3K4A') {
          photoLavieBrandId = id;
          photoLavieBrandName = obj.name || 'K-Photo LaVie';
          break;
        }
      }
      if (!photoLavieBrandId && brandsSnap.docs.length > 0) {
        photoLavieBrandId = brandsSnap.docs[0].id;
        photoLavieBrandName = brandsSnap.docs[0].data()['name'] || 'K-Photo LaVie';
      }

      const storeSnap = await getDocs(collection(this.firestore, 'stores'));
      const storeMap = new Map<string, any>();
      storeSnap.forEach(d => {
        const sData = d.data();
        storeMap.set(d.id, sData);
        if (sData['storeId']) storeMap.set(String(sData['storeId']).trim(), sData);
      });

      const compSnap = await getDocs(collection(this.firestore, 'company'));
      const compMap = new Map<string, any>();
      compSnap.forEach(d => {
        compMap.set(d.id, d.data());
      });

      const devSnap = await getDocs(collection(this.firestore, 'devices'));
      this.deviceBrandLogs.push(`Found ${devSnap.size} device documents. Queuing ALL active devices for reassignment to '${photoLavieBrandName}'...`);

      for (const d of devSnap.docs) {
        if (d.id === 'global' || d.data()['deviceId'] === 'global') continue;
        const devData = d.data();
        const deviceId = d.id;
        const storeId = String(devData['storeId'] || devData['storeDocId'] || '').trim();
        const store = storeId ? storeMap.get(storeId) : null;
        let companyName = 'N/A';

        if (store) {
          const companyId = String(store['companyId'] || store['companyDocId'] || store['company'] || '').trim();
          if (companyId) {
            const company = compMap.get(companyId);
            if (company) {
              companyName = company['company'] || companyId;
            }
          }
        }

        this.deviceBrandPreview.push({
          deviceId,
          storeId: storeId || 'N/A',
          companyName,
          resolvedBrandId: photoLavieBrandId,
          brandName: photoLavieBrandName
        });
      }

      this.hasPreviewedDeviceBrands = true;
      this.deviceBrandLogs.push(`Scan complete!`);
      this.deviceBrandLogs.push(`• ${this.deviceBrandPreview.length} devices queued to be reset/reassigned to '${photoLavieBrandName}'.`);

    } catch (err: any) {
      console.error('Error previewing device brand reassignment:', err);
      this.deviceBrandLogs.push(`Error: ${err.message || err}`);
      alert('Error during preview. See logs.');
    } finally {
      this.isBackfillingDeviceBrands = false;
    }
  }

  async fetchClaimedSessions() {
    this.isFetchingClaimedSessions = true;
    this.claimedSessions = [];
    try {
      const sessionsQuery = query(collectionGroup(this.firestore, 'sessions'));
      const snap = await getDocs(sessionsQuery);
      const claimed: any[] = [];
      snap.forEach(d => {
        const data = d.data();
        if (data['claimedByEmails'] && data['claimedByEmails'].length > 0) {
          claimed.push({
            sessionId: d.id,
            deviceId: data['DeviceId'] || 'Unknown',
            emails: data['claimedByEmails'].join(', '),
            date: data['Timestamp'] ? data['Timestamp'].toDate().toLocaleString() : 'Unknown Date'
          });
        }
      });
      // Sort by date descending
      this.claimedSessions = claimed.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    } catch (e) {
      console.error('Error fetching claimed sessions:', e);
      alert('Error fetching claimed sessions. You might need a composite index for collectionGroup if it fails.');
    } finally {
      this.isFetchingClaimedSessions = false;
    }
  }

  async scanPPImages() {
    this.isScanningPP = true;
    this.ppImagesToDelete = [];
    try {
      await this.scanStorageFlat('Photobooth/');
      if (this.ppImagesToDelete.length > 0) {
        if (confirm(`Found ${this.ppImagesToDelete.length} _PP files to delete. Proceed with deletion?`)) {
          await this.deletePPImages();
        } else {
          this.ppImagesToDelete = [];
        }
      } else {
        alert('No _PP files found.');
      }
    } catch (e) {
      console.error('Error scanning for _PP images:', e);
      alert('Error scanning for _PP images.');
    } finally {
      this.isScanningPP = false;
    }
  }

  private async scanStorageFlat(prefix: string) {
    const bucket = this.storage.app.options.storageBucket;
    if (!bucket) throw new Error("Storage bucket not configured.");

    const token = await this.auth.currentUser?.getIdToken();
    if (!token) throw new Error("Not authenticated");

    let pageToken = '';

    while (true) {
      const url = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o?prefix=${encodeURIComponent(prefix)}&maxResults=1000` + (pageToken ? `&pageToken=${pageToken}` : '');
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to list objects: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.items) {
        for (const item of data.items) {
          if (item.name) {
            const parts = item.name.split('/');
            const fileName = parts[parts.length - 1];

            // Extract filename without extension to check if it ends exactly with _PP
            const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.')) || fileName;

            if (nameWithoutExt.endsWith('_PP')) {
              const itemRef = ref(this.storage, item.name);
              this.ppImagesToDelete.push(itemRef);
            }
          }
        }
      }

      if (data.nextPageToken) {
        pageToken = data.nextPageToken;
      } else {
        break;
      }
    }
  }

  private async deletePPImages() {
    this.isCleaningPPImages = true;
    try {
      let count = 0;
      for (const item of this.ppImagesToDelete) {
        await deleteObject(item);
        count++;
      }
      alert(`Successfully deleted ${count} _PP files.`);
    } catch (e) {
      console.error('Error deleting _PP images:', e);
      alert('Error deleting some _PP images.');
    } finally {
      this.isCleaningPPImages = false;
      this.ppImagesToDelete = [];
    }
  }

  async onResetSmrsCompanySelected() {
    if (!this.resetSmrsCompanyId) return;

    try {
      const invoicesCol = collection(this.firestore, 'invoices');
      const q = query(
        invoicesCol,
        where('companyId', '==', this.resetSmrsCompanyId),
        orderBy('period', 'asc'),
        limit(1)
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        this.resetSmrsStartPeriod = snap.docs[0].data()['period'];
      }
    } catch (e) {
      console.error('Failed to fetch initial period:', e);
    }
  }

  resetSmrsBrandId = '';
  brands: any[] = [];
  private brandsSub: any;
  companies: any[] = [];
  private companiesSub: any;

  ngOnInit() {
    this.companiesSub = this.invoiceService.getCompanies().subscribe(comps => {
      if (comps && comps.length > 0) {
        this.companies = comps.sort((a, b) => (a.company || '').localeCompare(b.company || ''));
      }
    });
    this.brandsSub = this.brandService.getBrands().subscribe(brs => {
      if (brs && brs.length > 0) {
        this.brands = brs;
      }
    });

    // Proactive direct fetch fallback to ensure immediate availability
    getDocs(collection(this.firestore, 'company')).then(snap => {
      if (this.companies.length === 0 && !snap.empty) {
        this.companies = snap.docs
          .map(d => ({ ...d.data(), id: d.id }))
          .sort((a: any, b: any) => (a.company || '').localeCompare(b.company || ''));
      }
    });

    getDocs(collection(this.firestore, 'brands')).then(snap => {
      if (this.brands.length === 0 && !snap.empty) {
        this.brands = snap.docs.map(d => ({ ...d.data(), id: d.id }));
      }
    });
  }

  private isBrandMatch(company: any, targetBrandId: string): boolean {
    if (!targetBrandId) return true;
    const cBrand = String(company.brand || '').trim();
    const cBrandId = String(company.brandId || '').trim();

    // 1. Direct match with targetBrandId
    if (cBrandId === targetBrandId || cBrand === targetBrandId) return true;

    // 2. Check if targetBrandId maps to a brand object in this.brands
    const targetBrandObj = this.brands.find(b => b.id === targetBrandId);
    const targetBrandName = targetBrandObj ? String(targetBrandObj.name || '').trim().toLowerCase() : '';
    const norm = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

    const targetNorm = norm(targetBrandName || targetBrandId);
    const cBrandNorm = norm(cBrand);
    const cBrandIdNorm = norm(cBrandId);

    // Direct normalized name match
    if (cBrandNorm && (cBrandNorm === targetNorm || cBrandNorm === norm(targetBrandId))) return true;
    if (cBrandIdNorm && (cBrandIdNorm === targetNorm || cBrandIdNorm === norm(targetBrandId))) return true;

    // Alias matching for LFC (Life Four Cuts, LK, LFC)
    const isTargetLfc = targetNorm === 'lfc' || targetNorm === 'lk' || targetNorm.includes('fourcuts') || targetNorm.includes('4cuts');
    if (isTargetLfc) {
      const isCLfc = cBrandNorm === 'lfc' || cBrandNorm === 'lk' || cBrandNorm.includes('fourcuts') || cBrandNorm.includes('4cuts') ||
                     cBrandIdNorm === 'lfc' || cBrandIdNorm === 'lk' || cBrandIdNorm.includes('fourcuts') || cBrandIdNorm.includes('4cuts') ||
                     (company.company && (company.company.toUpperCase().includes('LFC') || company.company.toUpperCase().includes('LIFE FOUR')));
      if (isCLfc) return true;
    }

    // Alias matching for K-Photo Lavie
    const isTargetLavie = targetNorm.includes('photolavie') || targetNorm.includes('lavie') || targetNorm === 'kceg';
    if (isTargetLavie) {
      const isCLavie = cBrandNorm.includes('photolavie') || cBrandNorm.includes('lavie') || cBrandNorm === 'kceg' ||
                       cBrandIdNorm.includes('photolavie') || cBrandIdNorm.includes('lavie') || cBrandIdNorm === 'kceg' ||
                       cBrand === 'T0RdUUPStz2b979i3K4A' || cBrandId === 'T0RdUUPStz2b979i3K4A' ||
                       (company.company && (company.company.toUpperCase().includes('LAVIE') || company.company.toUpperCase().includes('PHOTO')));
      if (isCLavie) return true;
    }

    return false;
  }

  get filteredResetCompanies() {
    if (!this.resetSmrsBrandId) return this.companies;
    return this.companies.filter(c => this.isBrandMatch(c, this.resetSmrsBrandId));
  }

  get ledgerMigrationCompanies() {
    if (!this.ledgerMigrationBrandId) return this.companies;
    return this.companies.filter(c => this.isBrandMatch(c, this.ledgerMigrationBrandId));
  }

  ngOnDestroy() {
    if (this.companiesSub) {
      this.companiesSub.unsubscribe();
    }
    if (this.brandsSub) {
      this.brandsSub.unsubscribe();
    }
  }

  countryInput = '한국:82, 미국:1, 캐나다:1, 일본:81, 중국:86, 베트남:84, 태국:66, 필리핀:63, 인도네시아:62, 말레이시아:60, 싱가포르:65, 호주:61, 뉴질랜드:64, 영국:44, 프랑스:33, 독일:49, 이탈리아:39, 스페인:34, 러시아:7, 브라질:55, 멕시코:52, 인도:91, 홍콩:852, 대만:886, 네덜란드:31, 스위스:41, 스웨덴:46, 벨기에:32, 오스트리아:43, 노르웨이:47, 덴마크:45, 핀란드:358, 아일랜드:353, 포르투갈:351, 그리스:30, 튀르키예:90, 사우디아라비아:966, 아랍에미리트:971, 이스라엘:972, 남아공:27, 이집트:20';

  countryMap: { [key: string]: string } = {
    '한국': 'South Korea', '미국': 'USA', '캐나다': 'Canada', '일본': 'Japan', '중국': 'China',
    '베트남': 'Vietnam', '태국': 'Thailand', '필리핀': 'Philippines', '인도네시아': 'Indonesia',
    '말레이시아': 'Malaysia', '싱가포르': 'Singapore', '호주': 'Australia', '뉴질랜드': 'New Zealand',
    '영국': 'UK', '프랑스': 'France', '독일': 'Germany', '이탈리아': 'Italy', '스페인': 'Spain',
    '러시아': 'Russia', '브라질': 'Brazil', '멕시코': 'Mexico', '인도': 'India', '홍콩': 'Hong Kong',
    '대만': 'Taiwan', '네덜란드': 'Netherlands', '스위스': 'Switzerland', '스웨덴': 'Sweden',
    '벨기에': 'Belgium', '오스트리아': 'Austria', '노르웨이': 'Norway', '덴마크': 'Denmark',
    '핀란드': 'Finland', '아일랜드': 'Ireland', '포르투갈': 'Portugal', '그리스': 'Greece',
    '튀르키예': 'Turkey', '사우디아라비아': 'Saudi Arabia', '아랍에미리트': 'UAE', '이스라엘': 'Israel',
    '남아공': 'South Africa', '이집트': 'Egypt'
  };

  firebasePhotoLavie = (environment as any).firebasePhotoLavie ? JSON.stringify((environment as any).firebasePhotoLavie, null, 2) : '';
  migrationCollection = '';
  machines: any[] = [];
  isLoadingMachines = false;

  async onStoreMapFileChange(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      const file = target.files[0];
      this.isMapping = true;
      try {
        await this.excelService.importStoreMapping(file);
        alert('Store mapping imported successfully!');
      } catch (error) {
        console.error('Store mapping import failed', error);
        alert('Store mapping import failed. See console for details.');
      } finally {
        this.isMapping = false;
        target.value = ''; // Reset file input
      }
    }
  }

  async onUpdateStoreNamesFileChange(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      const file = target.files[0];
      this.isUpdatingNames = true;
      try {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { cellDates: true });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows: any[] = XLSX.utils.sheet_to_json(worksheet);

        const storesCol = collection(this.firestore, 'stores');
        const snapshot = await getDocs(storesCol);
        const storeMap = new Map<string, any>(); // storeId -> docRef

        snapshot.forEach(d => {
          const data = d.data();
          if (data['storeId']) {
            storeMap.set(String(data['storeId']).trim(), d.ref);
          }
        });

        const updates = new Map<any, any>(); // ref -> data

        for (const row of rows) {
          const storeId = String(row['지점 계정'] || '').trim();
          const name = String(row['지점명'] || '').trim();

          if (storeId && name && storeMap.has(storeId)) {
            updates.set(storeMap.get(storeId), { name });
          }
        }

        if (updates.size === 0) {
          alert('No matching stores found to update.');
          return;
        }

        const updateArray = Array.from(updates.entries());
        const chunkSize = 450;
        for (let i = 0; i < updateArray.length; i += chunkSize) {
          const chunk = updateArray.slice(i, i + chunkSize);
          const batch = writeBatch(this.firestore);
          chunk.forEach(([ref, data]) => batch.update(ref, data));
          await batch.commit();
        }

        alert(`Successfully updated names for ${updates.size} stores.`);

      } catch (error) {
        console.error('Error updating store names:', error);
        alert('Failed to update store names. See console for details.');
      } finally {
        this.isUpdatingNames = false;
        target.value = '';
      }
    }
  }

  async onUpdateMachineCountriesFileChange(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      const file = target.files[0];
      this.isUpdatingMachineCountries = true;
      try {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { cellDates: true });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows: any[] = XLSX.utils.sheet_to_json(worksheet);

        const updates = new Map<string, string>(); // docId -> country

        rows.forEach(row => {
          const machineId = String(row['Machine ID'] || '').trim();
          const countryKR = String(row['국가'] || '').trim();

          if (machineId && countryKR) {

            const docId = `machine_${machineId}`;
            updates.set(docId, countryKR);
          }
        });

        if (updates.size === 0) {
          alert('No valid rows found (Columns: Machine ID, 국가).');
          return;
        }

        if (!confirm(`Found ${updates.size} machines to update. Proceed?`)) return;

        // Use the machines service to update the remote database defined in firebasePhotoLavie config
        const updatedCount = await this.machinesService.updateMachineCountries(updates);

        alert(`Successfully updated Country field for ${updatedCount} machines.`);

      } catch (error) {
        console.error('Error updating machine countries:', error);
        alert('Failed to update machine countries. See console for details.');
      } finally {
        this.isUpdatingMachineCountries = false;
        target.value = '';
      }
    }
  }

  async syncStoreMachineCounts() {
    if (this.isSyncingStoreMachineCounts) return;
    this.isSyncingStoreMachineCounts = true;
    this.storeMachineCountSyncResult = null;
    try {
      const result = await this.storeService.syncMachineCounts();
      this.storeMachineCountSyncResult = `Successfully synchronized machine counts: updated ${result.updatedStores} stores (Total stores checked: ${result.totalStores}).`;
      alert(this.storeMachineCountSyncResult);
    } catch (e: any) {
      console.error('Failed to sync store machine counts:', e);
      this.storeMachineCountSyncResult = `Error: ${e?.message || 'Failed to sync store machine counts'}`;
      alert(this.storeMachineCountSyncResult);
    } finally {
      this.isSyncingStoreMachineCounts = false;
    }
  }

  async removeMachineCountFromStores() {
    if (this.isRemovingMachineCounts) return;
    if (!confirm('Are you sure you want to remove the legacy "machineCount" field from all documents in the "stores" collection? This will delete the "machineCount" field while preserving "deviceCount".')) {
      return;
    }

    this.isRemovingMachineCounts = true;
    this.removeMachineCountsResult = null;
    try {
      const result = await this.storeService.removeMachineCountField();
      this.removeMachineCountsResult = `Successfully removed "machineCount" field from ${result.removedStores} store documents (Total stores examined: ${result.totalStores}).`;
      alert(this.removeMachineCountsResult);
    } catch (e: any) {
      console.error('Failed to remove machineCount from stores:', e);
      this.removeMachineCountsResult = `Error: ${e?.message || 'Failed to remove machineCount from stores'}`;
      alert(this.removeMachineCountsResult);
    } finally {
      this.isRemovingMachineCounts = false;
    }
  }

  async initCountries() {
    if (!confirm('This will initialize/reset the Country lookup table. Continue?')) return;

    const countries = this.countryInput.split(',').map(c => {
      const parts = c.split(':');
      return { name: parts[0].trim(), code: parts.length > 1 ? parts[1].trim() : '' };
    }).filter(c => c.name);

    try {
      await this.countryService.initializeCountries(countries);
      const names = countries.map(c => c.name).join(', ');
      alert(`Countries initialized successfully (${names}).`);
    } catch (error) {
      console.error('Failed to initialize countries', error);
      alert('Failed to initialize countries. See console.');
    }
  }

  async initChartOfAccounts() {
    if (!confirm('This will initialize the Chart of Accounts in the "chart_of_account" collection. Continue?')) return;

    const coaData = [
      { category: "Expense", code: "6020", name: "Bank Service Charges" },
      { category: "Expense", code: "6200", name: "Repairs and Maintenance" },
      { category: "Expense", code: "6070", name: "Insurance Expense" },
      { category: "Expense", code: "6150", name: "Payroll Tax" },
      { category: "Expense", code: "6240", name: "Taxes" },
      { category: "Expense", code: "6180", name: "Professional Fee" },
      { category: "Revenue", code: "4200", name: "Refund" },
      { category: "Expense", code: "6060", name: "Guarantee Payment" },
      { category: "Expense", code: "6270", name: "Travel Expense" },
      { category: "Expense", code: "6100", name: "Material Supplies" },
      { category: "Revenue", code: "4030", name: "Supplies Sales" },
      { category: "Revenue", code: "4010", name: "Cash Sales" },
      { category: "Revenue", code: "4020", name: "Credit Card Sales" },
      { category: "Expense", code: "6260", name: "Transportation" },
      { category: "Expense", code: "6050", name: "Equipment Rental" },
      { category: "Expense", code: "6140", name: "Payroll Expenses" },
      { category: "Expense", code: "6030", name: "Computer and Internet Expenses" },
      { category: "Expense", code: "6210", name: "Royalty" },
      { category: "Expense", code: "6230", name: "Storage" },
      { category: "Expense", code: "6090", name: "Management fee" },
      { category: "Liability", code: "2100", name: "Loan" },
      { category: "Expense", code: "6280", name: "Utilities" },
      { category: "Expense", code: "6250", name: "Telephone Expense" },
      { category: "Expense", code: "6195", name: "Rent Expense-Other" },
      { category: "Expense", code: "6120", name: "Merchant Account Fees" },
      { category: "Expense", code: "6010", name: "Advertising and Promotion" },
      { category: "Expense", code: "6170", name: "Postage and Delivery" },
      { category: "Expense", code: "6040", name: "Dues and Subscriptions" },
      { category: "Expense", code: "6110", name: "Meals and Entertainment" },
      { category: "Expense", code: "6130", name: "Office Supplies" },
      { category: "Liability", code: "2200", name: "Sales Tax" },
      { category: "Expense", code: "6190", name: "Rent Expense" },
      { category: "Expense", code: "6160", name: "Penalty" },
      { category: "Expense", code: "6220", name: "Shipping & Delivery Fees" }
    ];

    try {
      const batch = writeBatch(this.firestore);
      const colRef = collection(this.firestore, 'chart_of_account');

      coaData.forEach(account => {
        const docRef = doc(colRef, account.code);
        batch.set(docRef, { ...account, id: account.code });
      });

      await batch.commit();
      alert('Chart of Accounts initialized successfully.');
    } catch (error) {
      console.error('Error initializing COA:', error);
      alert('Failed to initialize Chart of Accounts.');
    }
  }

  async initRoles() {
    if (!confirm('This will initialize the Roles (admin, manager, user) in the "roles" collection. Continue?')) return;

    const roles = [
      { name: 'admin', description: 'Administrator with full access' },
      { name: 'manager', description: 'Manager with access to reports and invoices' },
      { name: 'user', description: 'Standard user with access to invoices' }
    ];

    try {
      const batch = writeBatch(this.firestore);
      const colRef = collection(this.firestore, 'roles');

      roles.forEach(role => {
        const docRef = doc(colRef, role.name); // Use name as ID for uniqueness
        batch.set(docRef, role);
      });

      await batch.commit();
      alert('Roles initialized successfully.');
    } catch (error) {
      console.error('Error initializing roles:', error);
      alert('Failed to initialize roles.');
    }
  }

  async migrateCollection() {
    if (!this.firebasePhotoLavie || !this.migrationCollection) {
      alert('Please provide Source Config JSON and Collection Name.');
      return;
    }

    try {
      const config = JSON.parse(this.firebasePhotoLavie);
      // Initialize secondary app with a unique name to avoid conflicts
      const appName = 'migrationSource_' + Date.now();
      const sourceApp = initializeApp(config, appName);
      const sourceDb = getSdkFirestore(sourceApp);

      const sourceColRef = collection(sourceDb, this.migrationCollection);
      const snapshot = await getDocs(sourceColRef);

      if (snapshot.empty) {
        alert(`No documents found in source collection: ${this.migrationCollection}`);
        return;
      }

      const docs = snapshot.docs.map(d => ({ id: d.id, data: d.data() }));
      const batchSize = 450;
      const targetColRef = collection(this.firestore, this.migrationCollection);

      for (let i = 0; i < docs.length; i += batchSize) {
        const chunk = docs.slice(i, i + batchSize);
        const batch = writeBatch(this.firestore);

        chunk.forEach(item => {
          const docRef = doc(targetColRef, item.id);
          batch.set(docRef, item.data);
        });

        await batch.commit();
      }

      alert(`Successfully migrated ${docs.length} documents from ${this.migrationCollection}.`);
    } catch (error) {
      console.error('Migration failed', error);
      alert('Migration failed. See console for details.');
    }
  }

  async updateBrandLkToLfc() {
    if (!confirm('This will update all companies with the brand "LK" to "LFC". Continue?')) return;

    this.isUpdatingBrand = true;
    try {
      const companiesCol = collection(this.firestore, 'company');
      const q = query(companiesCol, where('brand', '==', 'LK'));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        alert('No companies found with brand "LK".');
        return;
      }

      let updatedCount = 0;
      const batchSize = 450;
      const docs = snapshot.docs;

      for (let i = 0; i < docs.length; i += batchSize) {
        const chunk = docs.slice(i, i + batchSize);
        const batch = writeBatch(this.firestore);

        chunk.forEach(d => {
          batch.update(d.ref, { brand: 'LFC' });
          updatedCount++;
        });

        await batch.commit();
      }

      alert(`Successfully updated ${updatedCount} companies from LK to LFC.`);
    } catch (error) {
      console.error('Error updating brands:', error);
      alert('Failed to update brands. See console for details.');
    } finally {
      this.isUpdatingBrand = false;
    }
  }

  async updateSmrsRatesBrandLkToLfc() {
    if (!confirm('This will update all SMRS rates with the brand "LK" to "LFC". Continue?')) return;

    this.isUpdatingSmrsRatesBrand = true;
    try {
      // Note: Assuming the collection is named 'smrs_rates'. If it is 'rates', please change it below.
      const ratesCol = collection(this.firestore, 'smrs_rates');
      const q = query(ratesCol, where('brand', '==', 'LK'));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        alert('No SMRS rates found with brand "LK".');
        return;
      }

      let updatedCount = 0;
      const batchSize = 450;
      const docs = snapshot.docs;

      for (let i = 0; i < docs.length; i += batchSize) {
        const chunk = docs.slice(i, i + batchSize);
        const batch = writeBatch(this.firestore);

        chunk.forEach(d => {
          batch.update(d.ref, { brand: 'LFC' });
          updatedCount++;
        });

        await batch.commit();
      }

      alert(`Successfully updated ${updatedCount} SMRS rates from LK to LFC.`);
    } catch (error) {
      console.error('Error updating brands:', error);
      alert('Failed to update brands. See console for details.');
    } finally {
      this.isUpdatingSmrsRatesBrand = false;
    }
  }

  async migrateBrandsToIds() {
    if (!confirm('This will update all companies and SMRS rates to use brand IDs instead of brand names. Continue?')) return;

    this.isMigratingBrands = true;
    try {
      // 1. Fetch all brands and create a name-to-id map
      const brandsCol = collection(this.firestore, 'brands');
      const brandsSnap = await getDocs(brandsCol);
      const brandNameToId = new Map<string, string>();

      brandsSnap.forEach(doc => {
        const data = doc.data();
        if (data['name']) {
          brandNameToId.set(data['name'], doc.id);
        }
      });

      if (brandNameToId.size === 0) {
        alert('No brands found in the database. Please create brands first.');
        return;
      }

      let updatedCompanyCount = 0;
      let updatedRatesCount = 0;
      const batchSize = 450;

      // 2. Migrate 'company' collection
      const companyCol = collection(this.firestore, 'company');
      const companySnap = await getDocs(companyCol);
      const companyDocsToUpdate: any[] = [];

      companySnap.forEach(doc => {
        const data = doc.data();
        const currentBrand = data['brand'];
        // If the currentBrand matches a string name, we queue it for update
        if (currentBrand && brandNameToId.has(currentBrand)) {
          companyDocsToUpdate.push({ ref: doc.ref, newBrandId: brandNameToId.get(currentBrand) });
        }
      });

      for (let i = 0; i < companyDocsToUpdate.length; i += batchSize) {
        const chunk = companyDocsToUpdate.slice(i, i + batchSize);
        const batch = writeBatch(this.firestore);
        chunk.forEach(item => {
          batch.update(item.ref, { brand: item.newBrandId });
          updatedCompanyCount++;
        });
        await batch.commit();
      }

      // 3. Migrate 'smrs_rates' collection
      const ratesCol = collection(this.firestore, 'smrs_rates');
      const ratesSnap = await getDocs(ratesCol);
      const ratesDocsToUpdate: any[] = [];

      ratesSnap.forEach(doc => {
        const data = doc.data();
        const currentBrand = data['brand'];
        if (currentBrand && brandNameToId.has(currentBrand)) {
          ratesDocsToUpdate.push({ ref: doc.ref, newBrandId: brandNameToId.get(currentBrand) });
        }
      });

      for (let i = 0; i < ratesDocsToUpdate.length; i += batchSize) {
        const chunk = ratesDocsToUpdate.slice(i, i + batchSize);
        const batch = writeBatch(this.firestore);
        chunk.forEach(item => {
          batch.update(item.ref, { brand: item.newBrandId });
          updatedRatesCount++;
        });
        await batch.commit();
      }

      alert(`Successfully migrated brands to IDs. Updated ${updatedCompanyCount} companies and ${updatedRatesCount} SMRS rates.`);
    } catch (error) {
      console.error('Error migrating brands to IDs:', error);
      alert('Failed to migrate brands to IDs. See console for details.');
    } finally {
      this.isMigratingBrands = false;
    }
  }

  /**
   * Reassign Company brand IDs:
   * - Any company with brand "P3Q4LSohGvHglFtHu9OF" (LFC) -> update to "9wne793dXs1zI00AAmdk"
   * - Any company with brand "T0RdUUPStz2b979i3K4A" (K-Photo LaVie) -> update to "djrizY8FlIVsYK8vBN4t"
   */
  async reassignCompanyBrandIds() {
    const lfcOldId = 'P3Q4LSohGvHglFtHu9OF';
    const lfcNewId = '9wne793dXs1zI00AAmdk';
    const kplOldId = 'T0RdUUPStz2b979i3K4A';
    const kplNewId = 'djrizY8FlIVsYK8vBN4t';

    if (!confirm(`This will update all companies in the 'company' collection:\n- '${lfcOldId}' (LFC) -> '${lfcNewId}'\n- '${kplOldId}' (K-Photo LaVie) -> '${kplNewId}'\n\nContinue?`)) {
      return;
    }

    this.isReassigningCompanyBrandIds = true;
    try {
      const compSnap = await getDocs(collection(this.firestore, 'company'));
      let lfcUpdatedCount = 0;
      let kplUpdatedCount = 0;
      let otherUpdatedCount = 0;
      const updates: Array<{ ref: any, patch: any, companyName: string, fromId: string, toId: string }> = [];

      compSnap.forEach(d => {
        const data = d.data();
        const rawBrand = String(data['brand'] || '').trim();
        const rawBrandId = String(data['brandId'] || '').trim();
        const companyName = data['company'] || d.id;

        let targetNewId: string | null = null;
        let matchedOldId = '';

        if (rawBrand === lfcOldId || rawBrandId === lfcOldId) {
          targetNewId = lfcNewId;
          matchedOldId = lfcOldId;
          lfcUpdatedCount++;
        } else if (rawBrand === kplOldId || rawBrandId === kplOldId) {
          targetNewId = kplNewId;
          matchedOldId = kplOldId;
          kplUpdatedCount++;
        }

        if (targetNewId) {
          updates.push({
            ref: d.ref,
            patch: {
              brand: targetNewId,
              brandId: targetNewId,
              updatedAt: new Date()
            },
            companyName,
            fromId: matchedOldId,
            toId: targetNewId
          });
        }
      });

      if (updates.length === 0) {
        alert(`No companies found matching brand IDs '${lfcOldId}' or '${kplOldId}'. All companies already up to date.`);
        return;
      }

      // Execute in Firestore batches of 450
      const batchSize = 450;
      for (let i = 0; i < updates.length; i += batchSize) {
        const chunk = updates.slice(i, i + batchSize);
        const batch = writeBatch(this.firestore);
        chunk.forEach(u => batch.update(u.ref, u.patch));
        await batch.commit();
      }

      console.log(`Reassigned ${updates.length} companies:`, updates);
      alert(`Successfully reassigned brand IDs for ${updates.length} companies:\n- LFC (${lfcOldId} -> ${lfcNewId}): ${lfcUpdatedCount}\n- K-Photo LaVie (${kplOldId} -> ${kplNewId}): ${kplUpdatedCount}`);
    } catch (error) {
      console.error('Error reassigning company brand IDs:', error);
      alert('Failed to reassign company brand IDs. See console for details.');
    } finally {
      this.isReassigningCompanyBrandIds = false;
    }
  }

  async remapBrandsToExistingIds() {
    if (!confirm('This will resolve the existing brand IDs for "LFC" and "K Photolavie", then remap all company, store, device, and SMRS rate records to those exact brand IDs. Continue?')) return;

    this.isRemappingBrands = true;
    try {
      // 1. Fetch all brands
      const brandsCol = collection(this.firestore, 'brands');
      const brandsSnap = await getDocs(brandsCol);
      const allBrands = brandsSnap.docs.map(d => ({ id: d.id, ...d.data() } as any));

      // 2. Identify LFC and K Photolavie brand IDs
      // Normalize name matching with alphanumeric regex
      const normalizeBrandKey = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

      const lfcBrand = allBrands.find(b => {
        const key = normalizeBrandKey(b.name);
        return key === 'lfc' || key === 'lk' || key.includes('fourcuts') || key.includes('4cuts');
      });

      const photolavieBrand = allBrands.find(b => {
        const key = normalizeBrandKey(b.name);
        return key.includes('photolavie') || key.includes('lavie');
      });

      if (!lfcBrand && !photolavieBrand) {
        alert('Could not locate existing brand documents for LFC or K Photolavie in the "brands" collection. Please verify the brands collection.');
        return;
      }

      const lfcId = lfcBrand?.id;
      const photolavieId = photolavieBrand?.id;

      const normalizeTarget = (val: string): string | null => {
        if (!val) return null;
        const raw = val.trim();
        const upper = raw.toUpperCase();
        // If already matching target ID
        if (lfcId && upper === lfcId.toUpperCase()) return lfcId;
        if (photolavieId && upper === photolavieId.toUpperCase()) return photolavieId;

        // Check if raw matches known source export ID for K-Photo Lavie
        if (raw === 'T0RdUUPStz2b979i3K4A') {
          return photolavieId || null;
        }

        const normKey = normalizeBrandKey(raw);

        // Check if matching K Photolavie alias / name / partial
        if (normKey.includes('photolavie') || normKey.includes('lavie') || normKey === 'kceg') {
          return photolavieId || null;
        }

        // Check if matching LFC alias / name
        if (normKey === 'lfc' || normKey === 'lk' || normKey.includes('fourcuts') || normKey.includes('4cuts')) {
          return lfcId || null;
        }

        return null;
      };

      let updatedCompany = 0;
      let updatedStores = 0;
      let updatedDevices = 0;
      let updatedRates = 0;
      const batchSize = 450;

      // 3. Remap 'company' collection
      const compSnap = await getDocs(collection(this.firestore, 'company'));
      const compUpdates: any[] = [];
      compSnap.forEach(d => {
        const data = d.data();
        const currentVal = String(data['brand'] || data['brandId'] || '').trim();
        const targetId = normalizeTarget(currentVal);
        if (targetId && (data['brand'] !== targetId || data['brandId'] !== targetId)) {
          compUpdates.push({ ref: d.ref, patch: { brand: targetId, brandId: targetId } });
        }
      });
      for (let i = 0; i < compUpdates.length; i += batchSize) {
        const chunk = compUpdates.slice(i, i + batchSize);
        const batch = writeBatch(this.firestore);
        chunk.forEach(u => {
          batch.update(u.ref, u.patch);
          updatedCompany++;
        });
        await batch.commit();
      }

      // 4. Remap 'stores' collection
      const storeSnap = await getDocs(collection(this.firestore, 'stores'));
      const storeUpdates: any[] = [];
      storeSnap.forEach(d => {
        const data = d.data();
        const currentVal = String(data['brand'] || data['brandId'] || '').trim();
        const targetId = normalizeTarget(currentVal);
        if (targetId && (data['brand'] !== targetId || data['brandId'] !== targetId)) {
          storeUpdates.push({ ref: d.ref, patch: { brand: targetId, brandId: targetId } });
        }
      });
      for (let i = 0; i < storeUpdates.length; i += batchSize) {
        const chunk = storeUpdates.slice(i, i + batchSize);
        const batch = writeBatch(this.firestore);
        chunk.forEach(u => {
          batch.update(u.ref, u.patch);
          updatedStores++;
        });
        await batch.commit();
      }

      // 5. Remap 'devices' collection
      const devSnap = await getDocs(collection(this.firestore, 'devices'));
      const devUpdates: any[] = [];
      devSnap.forEach(d => {
        const data = d.data();
        const currentVal = String(data['brand'] || data['brandId'] || '').trim();
        const targetId = normalizeTarget(currentVal);
        if (targetId && (data['brand'] !== targetId || data['brandId'] !== targetId)) {
          devUpdates.push({ ref: d.ref, patch: { brand: targetId, brandId: targetId } });
        }
      });
      for (let i = 0; i < devUpdates.length; i += batchSize) {
        const chunk = devUpdates.slice(i, i + batchSize);
        const batch = writeBatch(this.firestore);
        chunk.forEach(u => {
          batch.update(u.ref, u.patch);
          updatedDevices++;
        });
        await batch.commit();
      }

      // 6. Remap 'smrs_rates' collection
      const ratesSnap = await getDocs(collection(this.firestore, 'smrs_rates'));
      const ratesUpdates: any[] = [];
      ratesSnap.forEach(d => {
        const data = d.data();
        const currentVal = String(data['brand'] || data['brandId'] || '').trim();
        const targetId = normalizeTarget(currentVal);
        if (targetId && (data['brand'] !== targetId || data['brandId'] !== targetId)) {
          ratesUpdates.push({ ref: d.ref, patch: { brand: targetId, brandId: targetId } });
        }
      });
      for (let i = 0; i < ratesUpdates.length; i += batchSize) {
        const chunk = ratesUpdates.slice(i, i + batchSize);
        const batch = writeBatch(this.firestore);
        chunk.forEach(u => {
          batch.update(u.ref, u.patch);
          updatedRates++;
        });
        await batch.commit();
      }

      alert(
        `Brand remapping completed successfully!\n\n` +
        `Resolved Brand IDs:\n` +
        `- LFC ID: ${lfcId || 'None'}\n` +
        `- K Photolavie ID: ${photolavieId || 'None'}\n\n` +
        `Updated Records:\n` +
        `- Companies: ${updatedCompany}\n` +
        `- Stores: ${updatedStores}\n` +
        `- Devices: ${updatedDevices}\n` +
        `- SMRS Rates: ${updatedRates}`
      );
    } catch (error: any) {
      console.error('Error remapping brands:', error);
      alert(`Error remapping brands: ${error.message || error}`);
    } finally {
      this.isRemappingBrands = false;
    }
  }

  async fetchMachines() {
    this.isLoadingMachines = true;
    this.machines = [];

    try {
      this.machines = await this.machinesService.getMachines();
    } catch (error) {
      console.error('Error fetching machines:', error);
      alert('Failed to fetch machines. See console for details.');
    } finally {
      this.isLoadingMachines = false;
    }
  }

  async populateStoresFromMachines() {
    if (!confirm('This will fetch machines from PhotoLavie and populate the local stores collection with unique stores. Continue?')) return;
    this.isPopulatingStores = true;
    try {
      const machines = await this.machinesService.getMachines();
      const uniqueStores = new Map<string, any>();

      machines.forEach(machine => {
        const rawCountry = String(machine['Country'] || machine['country'] || '').trim();
        const country = this.countryMap[rawCountry] || rawCountry;
        const docId = String(machine.id || '');

        const userId = String(machine['userID'] || machine['userId'] || '');
        const storeId = `KPL_${country}${userId.substring(0, 6).toUpperCase()}`.trim();
        const storeName = `Store ${userId.substring(0, 6).toUpperCase()}`;

        if (storeId) {
          if (!uniqueStores.has(storeId)) {
            uniqueStores.set(storeId, {
              storeId: storeId,
              name: storeName || storeId,
              country: rawCountry,
              location: '',
              companyIds: []
            });
          }
        }
      });

      if (uniqueStores.size === 0) {
        alert('No valid stores found in machines data.');
        return;
      }

      const storesCol = collection(this.firestore, 'stores');
      const snapshot = await getDocs(storesCol);
      const existingStoreIds = new Set<string>();
      const softDeletedStoreRefs = new Map<string, any>();

      snapshot.forEach(d => {
        const data = d.data();
        const sid = data['storeId'] ? String(data['storeId']).trim() : null;
        if (sid) {
          if (data['isDeleted']) {
            softDeletedStoreRefs.set(sid, d.ref);
          } else {
            existingStoreIds.add(sid);
          }
        }
      });

      const storesToAdd: any[] = [];
      const storesToRestore: any[] = [];

      Array.from(uniqueStores.values()).forEach(s => {
        if (softDeletedStoreRefs.has(s.storeId)) {
          storesToRestore.push(softDeletedStoreRefs.get(s.storeId));
        } else if (!existingStoreIds.has(s.storeId)) {
          storesToAdd.push(s);
        }
      });

      if (storesToAdd.length === 0 && storesToRestore.length === 0) {
        alert('All stores from machines already exist in the database.');
        return;
      }

      const batchSize = 450;
      const allOps = [
        ...storesToAdd.map(store => ({ type: 'add' as const, store })),
        ...storesToRestore.map(ref => ({ type: 'restore' as const, ref }))
      ];

      for (let i = 0; i < allOps.length; i += batchSize) {
        const chunk = allOps.slice(i, i + batchSize);
        const batch = writeBatch(this.firestore);

        chunk.forEach(op => {
          if (op.type === 'add') {
            const docRef = doc(storesCol);
            batch.set(docRef, { ...op.store, createdAt: new Date() });
          } else if (op.type === 'restore') {
            batch.update(op.ref, { isDeleted: false });
          }
        });

        await batch.commit();
      }

      let successMsg = 'Successfully processed stores.';
      if (storesToAdd.length > 0 && storesToRestore.length > 0) {
        successMsg = `Successfully added ${storesToAdd.length} new stores and restored ${storesToRestore.length} soft-deleted stores.`;
      } else if (storesToAdd.length > 0) {
        successMsg = `Successfully added ${storesToAdd.length} new stores.`;
      } else if (storesToRestore.length > 0) {
        successMsg = `Successfully restored ${storesToRestore.length} soft-deleted stores.`;
      }
      alert(successMsg);
    } catch (error) {
      console.error('Error populating stores:', error);
      alert('Failed to populate stores. See console for details.');
    } finally {
      this.isPopulatingStores = false;
    }
  }

  async deletePopulatedStores() {
    if (!confirm('This will delete all stores in the local database that match the generated Store IDs from PhotoLavie machines. Continue?')) return;
    this.isDeletingStores = true;
    try {
      const machines = await this.machinesService.getMachines();
      const machineStoreIds = new Set<string>();

      machines.forEach(machine => {
        const rawCountry = String(machine['Country'] || machine['country'] || '').trim();
        const country = this.countryMap[rawCountry] || rawCountry;
        const userId = String(machine['userID'] || machine['userId'] || '');
        const storeId = `KPL_${country}${userId.substring(0, 6).toUpperCase()}`.trim();
        if (storeId) {
          machineStoreIds.add(storeId);
        }
      });

      if (machineStoreIds.size === 0) {
        alert('No valid stores found in machines data to delete.');
        return;
      }

      const storesCol = collection(this.firestore, 'stores');
      const snapshot = await getDocs(storesCol);

      const docsToDelete: any[] = [];
      snapshot.forEach(d => {
        const data = d.data();
        if (data['storeId'] && machineStoreIds.has(String(data['storeId']).trim())) {
          docsToDelete.push(d.ref);
        }
      });

      if (docsToDelete.length === 0) {
        alert('No matching populated stores found in the database.');
        return;
      }

      const batchSize = 450;
      for (let i = 0; i < docsToDelete.length; i += batchSize) {
        const chunk = docsToDelete.slice(i, i + batchSize);
        const batch = writeBatch(this.firestore);
        chunk.forEach(ref => batch.delete(ref));
        await batch.commit();
      }

      alert(`Successfully deleted ${docsToDelete.length} stores.`);
    } catch (error) {
      console.error('Error deleting stores:', error);
      alert('Failed to delete stores. See console for details.');
    } finally {
      this.isDeletingStores = false;
    }
  }

  onMigrationPeriodChange() {
    this.hasPreviewedMM = false;
    this.migrationPreviewMM = [];
  }

  async previewPaidInvoicesToMM() {
    if (!this.migrationPeriodMM) {
      alert('Please specify a period (e.g., 2024/05)');
      return;
    }

    this.isPreviewingMM = true;
    this.hasPreviewedMM = false;
    this.migrationPreviewMM = [];

    try {
      const period = this.migrationPeriodMM;
      const invoices = await this.invoiceService.getInvoices(period);
      const companies = await firstValueFrom(this.invoiceService.getCompanies());
      const stores = await firstValueFrom(this.invoiceService.getStores());
      const aggs = await this.invoiceService.getAggregations(period);

      const ratesCol = collection(this.firestore, 'smrs_rates');
      const qRates = query(ratesCol, where('period', '==', period));
      const ratesSnap = await getDocs(qRates);
      const rates = ratesSnap.docs.map(d => d.data() as any);

      const aggDataMap = this.invoiceService.calculateRevenueAndFees(aggs, stores, rates, period);

      const companyMap = new Map<string, any>();
      companies.forEach((c: any) => companyMap.set(c.id, c));

      const companyCurrencyMap = new Map<string, string>();
      stores.forEach((s: any) => {
        const country = s['country'] || '미국';
        const currency = s['currency'] || (['Canada', '캐나다'].includes(country) ? 'CAD' : 'USD');
        const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
        cids.forEach((cid: string) => {
          if (currency !== 'USD') {
            companyCurrencyMap.set(cid, currency);
          } else if (!companyCurrencyMap.has(cid)) {
            companyCurrencyMap.set(cid, 'USD');
          }
        });
      });

      for (const inv of invoices) {
        const companyId = inv['companyId'];
        if (!companyId) continue;

        const company = companyMap.get(companyId);
        if (!company || company.isFeeCollection) continue;

        const aggData = aggDataMap.get(`${period}_${companyId}`);
        const totalLfcFee = aggData?.lfcFee || 0;
        const totalKcegFee = aggData?.kcegFee || 0;
        const totalFee = aggData?.fee || 0;
        const beginningBalance = inv.beginningBalanceOverride ?? await this.invoiceService.getBeginningBalance(companyId, period);
        const totalDue = beginningBalance + totalFee;

        let amountPaid = 0;
        if (inv['amountPaid'] !== undefined) {
          amountPaid = Number(inv['amountPaid']);
        } else if (inv['remainingBalance'] !== undefined) {
          amountPaid = Math.round((totalDue - Number(inv['remainingBalance'])) * 100) / 100;
        } else if (inv['status'] === 'Paid') {
          amountPaid = totalDue;
        }

        // Show all paid invoices for re-transfer, regardless of whether they were already transferred.
        if (amountPaid > 0) {
          const currency = companyCurrencyMap.get(companyId) || 'USD';
          let exchangeRate = 1;
          if (currency !== 'USD') {
            const rateData = await this.invoiceService.getExchangeRate(currency, period);
            if (rateData) {
              exchangeRate = rateData.rate;
            } else {
              try {
                const apiData = await this.exchangeRateService.getCADtoUSDRate(period);
                if (apiData) exchangeRate = apiData.rate;
              } catch (e) {
                console.warn('Failed to fetch exchange rate fallback', e);
              }
            }
          }

          let previousAmount = 0;
          let currentAmount = 0;

          if (beginningBalance > 0) {
            previousAmount = Math.min(amountPaid, beginningBalance);
            currentAmount = Math.max(0, amountPaid - beginningBalance);
          } else {
            currentAmount = amountPaid;
          }

          let previousPeriod = '';
          if (previousAmount > 0) {
            const [yearStr, monthStr] = period.split('/');
            let year = parseInt(yearStr, 10);
            let month = parseInt(monthStr, 10);
            month -= 1;
            if (month === 0) {
              month = 12;
              year -= 1;
            }
            previousPeriod = `${year}/${month.toString().padStart(2, '0')}`;
          }

          const invoiceRow = { company, period, currency, exchangeRate, totalLfcFee, totalKcegFee, totalDue };

          this.migrationPreviewMM.push({
            companyName: company.company,
            amount: amountPaid,
            currentAmount: currentAmount,
            previousAmount: previousAmount,
            previousPeriod: previousPeriod,
            amountUSD: currentAmount * exchangeRate,
            currency: currency,
            invoiceRow: invoiceRow
          });
        }
      }
      this.migrationPreviewMM.sort((a, b) => a.companyName.localeCompare(b.companyName));
      this.hasPreviewedMM = true;
    } catch (error) {
      console.error('Error previewing MM migration:', error);
      alert('Failed to load preview. See console for details.');
    } finally {
      this.isPreviewingMM = false;
    }
  }

  async migratePaidInvoicesToMM() {
    if (this.migrationPreviewMM.length === 0) return;
    if (!confirm(`This will DELETE ALL existing Money Market transfers for ${this.migrationPeriodMM} and re-migrate ${this.migrationPreviewMM.length} paid invoices. This is irreversible. Continue?`)) return;

    this.isMigratingMM = true;
    try {
      const deletedCount = await this.invoiceService.deleteFeeCollectionTransfersForPeriod(this.migrationPeriodMM);
      let transferCount = 0;
      for (const item of this.migrationPreviewMM) {
        if (item.currentAmount > 0) {
          const transferResult = await this.invoiceService.transferToFeeCollection(item.invoiceRow, item.currentAmount, item.currentAmount);
          if (transferResult) transferCount++;
        }
      }
      alert(`Successfully deleted ${deletedCount} existing transfers and re-migrated ${transferCount} amounts to Money Market.`);
      this.hasPreviewedMM = false;
      this.migrationPreviewMM = [];
    } catch (error) {
      console.error('Error migrating to MM:', error);
      alert('Failed to migrate to MM. See console for details.');
    } finally {
      this.isMigratingMM = false;
    }
  }

  async resetSmrsInvoices() {
    if (!this.resetSmrsStartPeriod || !this.resetSmrsEndPeriod || !this.resetSmrsCompanyId) {
      alert('Please provide Start Period, End Period, and select a Company.');
      return;
    }

    const companyName = this.companies.find(c => c.id === this.resetSmrsCompanyId)?.company || 'Unknown Company';
    if (!confirm(`This will set remainingBalance to 0 and status to "Not Paid" for all invoices of ${companyName} from ${this.resetSmrsStartPeriod} to ${this.resetSmrsEndPeriod}. Continue?`)) return;

    this.isResettingSmrs = true;
    try {
      const invoicesCol = collection(this.firestore, 'invoices');
      // Fetch all invoices from startPeriod onwards to propagate balances correctly
      const q = query(
        invoicesCol,
        where('companyId', '==', this.resetSmrsCompanyId),
        where('period', '>=', this.resetSmrsStartPeriod)
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        alert('No invoices found starting from this period.');
        this.isResettingSmrs = false;
        return;
      }

      // Sort chronologically
      const docs = snapshot.docs.sort((a, b) => a.data()['period'].localeCompare(b.data()['period']));

      const docsByPeriod = new Map<string, any>();
      docs.forEach(d => docsByPeriod.set(d.data()['period'], d));

      const maxDocPeriod = docs.length > 0 ? docs[docs.length - 1].data()['period'] : this.resetSmrsStartPeriod;
      const actualEndPeriod = this.resetSmrsEndPeriod > maxDocPeriod ? this.resetSmrsEndPeriod : maxDocPeriod;

      const allPeriods: string[] = [];
      let [y, m] = this.resetSmrsStartPeriod.split('/').map(Number);
      const [ey, em] = actualEndPeriod.split('/').map(Number);
      while (y < ey || (y === ey && m <= em)) {
        allPeriods.push(`${y}/${m.toString().padStart(2, '0')}`);
        m++;
        if (m > 12) { m = 1; y++; }
      }

      let updatedCount = 0;
      let createdCount = 0;
      let lastDelta = 0;
      const batchSize = 450;

      for (let i = 0; i < allPeriods.length; i += batchSize) {
        const chunk = allPeriods.slice(i, i + batchSize);
        const batch = writeBatch(this.firestore);

        chunk.forEach(period => {
          const existingDoc = docsByPeriod.get(period);

          if (existingDoc) {
            const data = existingDoc.data();
            if (period <= this.resetSmrsEndPeriod) {
              // Inside the reset range
              const oldPaid = data['amountPaid'] || 0;
              const oldRem = data['remainingBalance'] || 0;
              const oldTotalDue = oldPaid + oldRem;

              const newTotalDue = oldTotalDue + lastDelta;
              const newRem = newTotalDue;

              batch.update(existingDoc.ref, {
                remainingBalance: newRem,
                amountPaid: 0,
                actualAmountPaid: 0,
                displayAmountPaid: 0,
                status: 'Not Paid',
                appliedFrom: [],
                isEdited: true,
                updatedAt: new Date(),
                updatedBy: 'DevTool'
              });

              lastDelta = newRem - oldRem;
              updatedCount++;
            } else {
              // Outside reset range
              if (Math.abs(lastDelta) > 0.001) {
                const oldRem = data['remainingBalance'] || 0;
                const newRem = oldRem + lastDelta;
                batch.update(existingDoc.ref, {
                  remainingBalance: newRem,
                  updatedAt: new Date(),
                  updatedBy: 'DevTool'
                });
                updatedCount++;
              }
            }
          } else {
            // Missing month, create it to prevent breaks in the chain
            if (period <= this.resetSmrsEndPeriod) {
              const newRem = lastDelta;
              const newRef = doc(invoicesCol);
              batch.set(newRef, {
                companyId: this.resetSmrsCompanyId,
                period: period,
                status: 'Not Paid',
                emailStatus: 'Not Sent',
                remainingBalance: newRem,
                amountPaid: 0,
                actualAmountPaid: 0,
                displayAmountPaid: 0,
                createdAt: new Date(),
                updatedAt: new Date(),
                updatedBy: 'DevTool',
                isManual: true,
                isEdited: true
              });
              lastDelta = newRem; // oldRem was 0
              createdCount++;
            } else {
              if (Math.abs(lastDelta) > 0.001) {
                const newRem = lastDelta;
                const newRef = doc(invoicesCol);
                batch.set(newRef, {
                  companyId: this.resetSmrsCompanyId,
                  period: period,
                  status: 'Not Paid',
                  emailStatus: 'Not Sent',
                  remainingBalance: newRem,
                  amountPaid: 0,
                  actualAmountPaid: 0,
                  displayAmountPaid: 0,
                  createdAt: new Date(),
                  updatedAt: new Date(),
                  updatedBy: 'DevTool',
                  isManual: true,
                  isEdited: true
                });
                createdCount++;
              }
            }
          }
        });

        await batch.commit();
      }

      alert(`Successfully reset ${updatedCount} invoices and created ${createdCount} missing invoices.`);

      try {
        const deletedTransfers = await this.invoiceService.deleteFeeCollectionTransfersForSourceCompany(
          this.resetSmrsCompanyId,
          this.resetSmrsStartPeriod,
          actualEndPeriod
        );
        if (deletedTransfers > 0) {
          console.log(`Also deleted ${deletedTransfers} fee collection transfers for this company.`);
        }
      } catch (err) {
        console.error('Failed to delete fee collection transfers:', err);
      }
    } catch (error) {
      console.error('Error resetting SMRS invoices:', error);
      alert('Failed to reset SMRS invoices. See console for details.');
    } finally {
      this.isResettingSmrs = false;
    }
  }

  async previewInvoiceLedgerRecalculation() {
    if (!this.ledgerMigrationCompanyId) {
      alert('Please select a specific company first.');
      return;
    }

    this.isScanningLedger = true;
    this.hasPreviewedLedger = false;
    this.ledgerAuditRows = [];
    this.ledgerMigrationDiffs = [];
    this.ledgerMigrationLogs = ['Starting SMRS ledger audit for selected company...'];
    this.ledgerMigrationStats = { 
      totalChecked: 0, 
      totalPopulated: 0, 
      totalDiscrepancies: 0,
      missingInvoicesCount: 0
    };

    try {
      // 1. Ensure target company object exists
      let company = this.selectedLedgerCompany;
      if (!company) {
        const compDoc = await getDocs(query(collection(this.firestore, 'company'), where('__name__', '==', this.ledgerMigrationCompanyId)));
        if (!compDoc.empty) {
          company = { id: compDoc.docs[0].id, ...compDoc.docs[0].data() };
        }
      }

      if (!company) {
        this.ledgerMigrationLogs.push(`Company with ID ${this.ledgerMigrationCompanyId} not found.`);
        alert('Selected company could not be found.');
        return;
      }

      const companyId = company.id;
      const companyName = company.company || companyId;
      const isFeeCollection = company.isFeeCollection === true;
      this.ledgerMigrationLogs.push(`Auditing company: ${companyName} (${companyId})`);

      // 2. Fetch all stores & smrs_rates
      const storesSnap = await getDocs(collection(this.firestore, 'stores'));
      const allStores: any[] = storesSnap.docs.map(d => ({ ...d.data(), id: d.id }));

      const ratesSnap = await getDocs(collection(this.firestore, 'smrs_rates'));
      const allRates = ratesSnap.docs.map(d => ({ ...d.data(), id: d.id } as SmrsRate));

      // 3. Identify stores assigned to this company
      const companyStoreIds = new Set<string>();
      allStores.forEach(s => {
        const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
        if (cids.includes(companyId)) {
          const sid = String(s['storeId'] || s['id'] || '').trim();
          if (sid) companyStoreIds.add(sid);
        }
      });
      this.ledgerMigrationLogs.push(`Found ${companyStoreIds.size} stores mapped to ${companyName}.`);

      // 4. Fetch all invoice documents for this company in Firestore
      const invoicesCol = collection(this.firestore, 'invoices');
      const q = query(invoicesCol, where('companyId', '==', companyId));
      const invSnap = await getDocs(q);
      const existingInvoices = invSnap.docs.map(d => ({ id: d.id, ref: d.ref, data: d.data() }));

      // 5. Also query all monthly store aggregations for this company's stores or explicit companyId
      // to discover any periods where aggregations exist but invoice document might be missing!
      const aggCol = collection(this.firestore, 'monthly_store_aggregations');
      const aggSnap = await getDocs(aggCol);
      const allAggsForCompany: any[] = [];
      const aggregationPeriods = new Set<string>();

      aggSnap.docs.forEach(d => {
        const data = d.data();
        const sid = String(data['storeId'] || '').trim();
        const belongs = (data['companyId'] === companyId) || (companyStoreIds.has(sid) && !data['companyId']);
        if (belongs) {
          allAggsForCompany.push(data);
          if (data['period']) aggregationPeriods.add(data['period']);
        }
      });

      // Combine all unique periods from both invoices and aggregations
      const allPeriods = new Set<string>();
      existingInvoices.forEach(inv => {
        if (inv.data['period']) allPeriods.add(inv.data['period']);
      });
      aggregationPeriods.forEach(p => allPeriods.add(p));

      const sortedPeriods = Array.from(allPeriods).sort();
      this.ledgerMigrationLogs.push(`Found ${sortedPeriods.length} unique periods (${existingInvoices.length} invoice docs in Firestore).`);

      let carryForwardBalance = 0;

      for (const period of sortedPeriods) {
        this.ledgerMigrationStats.totalChecked++;
        const invDoc = existingInvoices.find(i => i.data['period'] === period);
        const data = invDoc ? invDoc.data : null;
        const hasExistingDoc = !!invDoc;

        // Current values from Firestore
        const currentDocId = invDoc ? invDoc.id : '(Not in Firestore)';
        const currentStatus = data ? (data['status'] || 'Not Paid') : 'Missing Document';
        const currentPaid = data ? Number(data['amountPaid'] || 0) : 0;
        const currentBegRaw = data ? data['beginningBalance'] : undefined;
        const currentFeeRaw = data ? data['totalFee'] : undefined;
        const currentDueRaw = data ? data['totalDue'] : undefined;
        const currentRemRaw = data ? data['remainingBalance'] : undefined;

        // Missing fields diagnosis
        const missingFields: string[] = [];
        if (!hasExistingDoc) {
          missingFields.push('Entire Invoice Doc Missing');
          this.ledgerMigrationStats.missingInvoicesCount++;
        } else {
          if (currentBegRaw === undefined) missingFields.push('beginningBalance');
          if (currentFeeRaw === undefined) missingFields.push('totalFee');
          if (currentDueRaw === undefined) missingFields.push('totalDue');
          if (currentRemRaw === undefined) missingFields.push('remainingBalance');
          if (missingFields.length > 0) {
            this.ledgerMigrationStats.totalPopulated++;
          }
        }

        // Projected calculation
        if (isFeeCollection) {
          const projectedBeg = 0;
          const projectedFee = 0;
          const projectedDue = 0;
          const projectedPaid = 0;
          const projectedRem = 0;
          const projectedStatus = 'Paid';

          const diffFound = !hasExistingDoc || (currentRemRaw !== 0);

          this.ledgerAuditRows.push({
            period,
            hasExistingDoc,
            currentDocId,
            currentStatus,
            currentBeg: currentBegRaw !== undefined ? Number(currentBegRaw) : null,
            currentFee: currentFeeRaw !== undefined ? Number(currentFeeRaw) : null,
            currentDue: currentDueRaw !== undefined ? Number(currentDueRaw) : null,
            currentPaid,
            currentRem: currentRemRaw !== undefined ? Number(currentRemRaw) : null,
            missingFields,
            projectedBeg,
            projectedFee,
            projectedDue,
            projectedPaid,
            projectedRem,
            projectedStatus,
            diffFound: diffFound || missingFields.length > 0
          });
          carryForwardBalance = 0;
          continue;
        }

        // Standard company calculation
        if (currentStatus === 'Canceled') {
          this.ledgerAuditRows.push({
            period,
            hasExistingDoc,
            currentDocId,
            currentStatus: 'Canceled',
            currentBeg: currentBegRaw !== undefined ? Number(currentBegRaw) : null,
            currentFee: currentFeeRaw !== undefined ? Number(currentFeeRaw) : null,
            currentDue: currentDueRaw !== undefined ? Number(currentDueRaw) : null,
            currentPaid,
            currentRem: currentRemRaw !== undefined ? Number(currentRemRaw) : null,
            missingFields,
            projectedBeg: 0,
            projectedFee: 0,
            projectedDue: 0,
            projectedPaid: currentPaid,
            projectedRem: 0,
            projectedStatus: 'Canceled',
            diffFound: false
          });
          carryForwardBalance = 0;
          continue;
        }

        // Determine projected beginning balance
        let projectedBeg = carryForwardBalance;
        if (data && data['beginningBalanceOverride'] !== undefined) {
          projectedBeg = Number(data['beginningBalanceOverride']);
        }

        // Compute projected fee from aggregations and rates
        const periodAggs = allAggsForCompany.filter(a => a['period'] === period);
        const periodRates = allRates.filter(r => r.period === period);
        let computedFee = 0;
        let matchedAggs = false;

        periodAggs.forEach(d => {
          if (d['revenues']) {
            matchedAggs = true;
            periodRates.forEach(r => {
              const val = this.invoiceService.calculateRevenueForRate(d, r);
              computedFee += val * (r.rate / 100);
            });
          }
        });

        computedFee = Math.round(computedFee * 100) / 100;

        // Fallback: If no aggregations matched but current doc has a totalFee, keep it!
        if (!matchedAggs && data && currentFeeRaw !== undefined && Number(currentFeeRaw) !== 0) {
          computedFee = Number(currentFeeRaw);
        } else if (!matchedAggs && data && currentFeeRaw === undefined && currentRemRaw !== undefined) {
          // Infer from current remaining if nothing else exists
          computedFee = Math.max(0, Math.round((Number(currentRemRaw) + currentPaid - projectedBeg) * 100) / 100);
        }

        const projectedDue = Math.round((projectedBeg + computedFee) * 100) / 100;
        // LOCKED PAYMENT FACT: amountPaid NEVER CHANGES!
        const projectedPaid = currentPaid;
        const projectedRem = Math.round((projectedDue - projectedPaid) * 100) / 100;

        let projectedStatus = 'Not Paid';
        if (projectedRem <= 0.01) {
          projectedStatus = 'Paid';
        } else if (projectedPaid > 0.01) {
          projectedStatus = 'Partial';
        }

        // Discrepancy checks against current stored numbers
        const curBegNum = currentBegRaw !== undefined ? Number(currentBegRaw) : null;
        const curFeeNum = currentFeeRaw !== undefined ? Number(currentFeeRaw) : null;
        const curDueNum = currentDueRaw !== undefined ? Number(currentDueRaw) : null;
        const curRemNum = currentRemRaw !== undefined ? Number(currentRemRaw) : null;

        const begDiff = curBegNum === null || Math.abs(curBegNum - projectedBeg) > 0.01;
        const feeDiff = curFeeNum === null || Math.abs(curFeeNum - computedFee) > 0.01;
        const dueDiff = curDueNum === null || Math.abs(curDueNum - projectedDue) > 0.01;
        const remDiff = curRemNum === null || Math.abs(curRemNum - projectedRem) > 0.01;
        const hasDiff = !hasExistingDoc || begDiff || feeDiff || dueDiff || remDiff;

        if (hasDiff) {
          this.ledgerMigrationStats.totalDiscrepancies++;
        }

        this.ledgerAuditRows.push({
          period,
          hasExistingDoc,
          currentDocId,
          currentStatus,
          currentBeg: curBegNum,
          currentFee: curFeeNum,
          currentDue: curDueNum,
          currentPaid,
          currentRem: curRemNum,
          missingFields,
          projectedBeg,
          projectedFee: computedFee,
          projectedDue,
          projectedPaid,
          projectedRem,
          projectedStatus,
          begDiff,
          feeDiff,
          dueDiff,
          remDiff,
          diffFound: hasDiff
        });

        carryForwardBalance = projectedRem;
      }

      this.ledgerMigrationDiffs = this.ledgerAuditRows.filter(r => r.diffFound || r.missingFields.length > 0);
      this.hasPreviewedLedger = true;
      this.ledgerMigrationLogs.push(`Audit complete! Checked ${this.ledgerMigrationStats.totalChecked} periods for ${companyName}.`);
      this.ledgerMigrationLogs.push(`Periods needing update: ${this.ledgerMigrationDiffs.length} (Discrepancies: ${this.ledgerMigrationStats.totalDiscrepancies}, Missing fields: ${this.ledgerMigrationStats.totalPopulated})`);
    } catch (error) {
      console.error('Error scanning invoice ledger:', error);
      this.ledgerMigrationLogs.push(`Error: ${error}`);
      alert('Failed to scan invoice ledger. Check console.');
    } finally {
      this.isScanningLedger = false;
    }
  }

  async executeInvoiceLedgerMigration() {
    const company = this.selectedLedgerCompany;
    if (!company) {
      alert('Please select a company to migrate.');
      return;
    }

    const companyName = company.company || company.id;
    if (!confirm(
      `⚠️ MIGRATE INVOICES FOR ${companyName.toUpperCase()}?\n\n` +
      `- Scope: ONLY ${companyName} (${this.ledgerAuditRows.length} periods)\n` +
      `- Safety Guarantee: Existing paid amounts (amountPaid) are LOCKED and will NOT change.\n` +
      `- Updates: Recalculates beginningBalance, totalFee, totalDue, and remainingBalance.\n\n` +
      `Proceed with migration for this company?`
    )) {
      return;
    }

    this.isRecalculatingLedger = true;
    this.ledgerMigrationLogs.push(`Starting ledger migration for ${companyName}...`);

    try {
      const storesSnap = await getDocs(collection(this.firestore, 'stores'));
      const allStores: any[] = storesSnap.docs.map(d => ({ ...d.data(), id: d.id }));

      const ratesSnap = await getDocs(collection(this.firestore, 'smrs_rates'));
      const allRates = ratesSnap.docs.map(d => ({ ...d.data(), id: d.id } as SmrsRate));

      const invoicesCol = collection(this.firestore, 'invoices');
      const q = query(invoicesCol, where('companyId', '==', company.id));
      const invSnap = await getDocs(q);

      // Determine earliest period
      const invoicePeriods = invSnap.docs
        .map(d => d.data()['period'])
        .filter(Boolean)
        .sort();

      const auditPeriods = this.ledgerAuditRows.map(r => r.period).filter(Boolean).sort();
      const earliestPeriod = (invoicePeriods[0] && auditPeriods[0])
        ? (invoicePeriods[0] < auditPeriods[0] ? invoicePeriods[0] : auditPeriods[0])
        : (invoicePeriods[0] || auditPeriods[0] || '2020/01');

      this.ledgerMigrationLogs.push(`Recalculating forward starting from period ${earliestPeriod}...`);
      await this.invoiceService.recalculateLedgerForward(company.id, earliestPeriod, allStores, allRates);

      this.ledgerMigrationLogs.push(`✓ Successfully migrated invoices for ${companyName}!`);
      alert(`Ledger migration completed successfully for ${companyName}!`);

      // Re-scan to show updated verified state
      await this.previewInvoiceLedgerRecalculation();
    } catch (error) {
      console.error('Error executing ledger migration:', error);
      this.ledgerMigrationLogs.push(`Execution error: ${error}`);
      alert(`Error during ledger migration: ${error}`);
    } finally {
      this.isRecalculatingLedger = false;
    }
  }

  // ==========================================
  // Loop Batch Migration Methods
  // ==========================================

  async batchScanCompaniesWithDiscrepancies() {
    const targetCompanies = this.ledgerMigrationCompanies;
    if (!targetCompanies || targetCompanies.length === 0) {
      alert('No companies found for the selected Brand filter.');
      return;
    }

    this.isBatchScanning = true;
    this.companiesWithDiscrepancies = [];
    this.ledgerMigrationLogs = [`Starting Batch Scan across ${targetCompanies.length} companies...`];
    this.batchProgressPercent = 0;
    this.batchProgressText = `Scanning 0 of ${targetCompanies.length}...`;

    try {
      const storesSnap = await getDocs(collection(this.firestore, 'stores'));
      const allStores: any[] = storesSnap.docs.map(d => ({ ...d.data(), id: d.id }));

      const ratesSnap = await getDocs(collection(this.firestore, 'smrs_rates'));
      const allRates = ratesSnap.docs.map(d => ({ ...d.data(), id: d.id } as SmrsRate));

      const aggSnap = await getDocs(collection(this.firestore, 'monthly_store_aggregations'));
      const allAggs: any[] = aggSnap.docs.map(d => d.data());

      const invoicesCol = collection(this.firestore, 'invoices');

      for (let idx = 0; idx < targetCompanies.length; idx++) {
        const company = targetCompanies[idx];
        const companyId = company.id;
        const companyName = company.company || companyId;
        const isFeeCollection = company.isFeeCollection === true;

        this.batchProgressPercent = Math.round(((idx + 1) / targetCompanies.length) * 100);
        this.batchProgressText = `Auditing ${idx + 1}/${targetCompanies.length}: ${companyName}...`;

        // Store IDs for this company
        const companyStoreIds = new Set<string>();
        allStores.forEach(s => {
          const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
          if (cids.includes(companyId)) {
            const sid = String(s['storeId'] || s['id'] || '').trim();
            if (sid) companyStoreIds.add(sid);
          }
        });

        // Company invoices
        const q = query(invoicesCol, where('companyId', '==', companyId));
        const invSnap = await getDocs(q);
        const existingInvoices = invSnap.docs.map(d => d.data());

        // Company aggregations
        const companyAggs = allAggs.filter(d => {
          const sid = String(d['storeId'] || '').trim();
          return (d['companyId'] === companyId) || (companyStoreIds.has(sid) && !d['companyId']);
        });

        const periodsSet = new Set<string>();
        existingInvoices.forEach(i => { if (i['period']) periodsSet.add(i['period']); });
        companyAggs.forEach(a => { if (a['period']) periodsSet.add(a['period']); });

        const sortedPeriods = Array.from(periodsSet).sort();
        if (sortedPeriods.length === 0) continue;

        let discrepancyCount = 0;
        let missingCount = 0;
        let carryForward = 0;

        for (const period of sortedPeriods) {
          const invData = existingInvoices.find(i => i['period'] === period);
          const hasDoc = !!invData;

          if (!hasDoc) {
            missingCount++;
            discrepancyCount++;
            continue;
          }

          if (isFeeCollection) {
            if (invData['beginningBalance'] === undefined || invData['totalFee'] === undefined || invData['remainingBalance'] !== 0) {
              discrepancyCount++;
            }
            continue;
          }

          if (invData['status'] === 'Canceled') {
            carryForward = 0;
            continue;
          }

          let projectedBeg = carryForward;
          if (invData['beginningBalanceOverride'] !== undefined) {
            projectedBeg = Number(invData['beginningBalanceOverride']);
          }

          const periodAggs = companyAggs.filter(a => a['period'] === period);
          const periodRates = allRates.filter(r => r.period === period);
          let computedFee = 0;
          let matchedAggs = false;

          periodAggs.forEach(d => {
            if (d['revenues']) {
              matchedAggs = true;
              periodRates.forEach(r => {
                const val = this.invoiceService.calculateRevenueForRate(d, r);
                computedFee += val * (r.rate / 100);
              });
            }
          });

          computedFee = Math.round(computedFee * 100) / 100;

          if (!matchedAggs && invData['totalFee'] !== undefined && Number(invData['totalFee']) !== 0) {
            computedFee = Number(invData['totalFee']);
          } else if (!matchedAggs && invData['totalFee'] === undefined && invData['remainingBalance'] !== undefined) {
            computedFee = Math.max(0, Math.round((Number(invData['remainingBalance']) + Number(invData['amountPaid'] || 0) - projectedBeg) * 100) / 100);
          }

          const projectedDue = Math.round((projectedBeg + computedFee) * 100) / 100;
          const paid = Number(invData['amountPaid'] || 0);
          const projectedRem = Math.round((projectedDue - paid) * 100) / 100;

          const curBeg = invData['beginningBalance'] !== undefined ? Number(invData['beginningBalance']) : null;
          const curFee = invData['totalFee'] !== undefined ? Number(invData['totalFee']) : null;
          const curDue = invData['totalDue'] !== undefined ? Number(invData['totalDue']) : null;
          const curRem = invData['remainingBalance'] !== undefined ? Number(invData['remainingBalance']) : null;

          if (curBeg === null || curFee === null || curDue === null || curRem === null) {
            missingCount++;
            discrepancyCount++;
          } else if (
            Math.abs(curBeg - projectedBeg) > 0.01 ||
            Math.abs(curFee - computedFee) > 0.01 ||
            Math.abs(curDue - projectedDue) > 0.01 ||
            Math.abs(curRem - projectedRem) > 0.01
          ) {
            discrepancyCount++;
          }

          carryForward = projectedRem;
        }

        if (discrepancyCount > 0 || missingCount > 0) {
          this.companiesWithDiscrepancies.push({
            companyId,
            companyName,
            earliestPeriod: sortedPeriods[0],
            discrepancyCount,
            missingCount,
            totalPeriods: sortedPeriods.length
          });
          this.ledgerMigrationLogs.push(`Found discrepancies in ${companyName}: ${discrepancyCount} discrepancy period(s), ${missingCount} missing doc/field(s).`);
        }
      }

      this.ledgerMigrationLogs.push(`Batch scan complete! Found ${this.companiesWithDiscrepancies.length} companies with discrepancies out of ${targetCompanies.length} audited.`);
      if (this.companiesWithDiscrepancies.length === 0) {
        alert('Great news! All companies are in 100% mathematical sync with zero discrepancies.');
      } else {
        alert(`Scan finished. Found ${this.companiesWithDiscrepancies.length} companies with discrepancies ready for migration.`);
      }
    } catch (err: any) {
      console.error('Error during batch scan:', err);
      this.ledgerMigrationLogs.push(`Batch scan error: ${err.message || err}`);
      alert(`Error during batch scan: ${err.message || err}`);
    } finally {
      this.isBatchScanning = false;
    }
  }

  async executeBatchMigrationInLoop() {
    if (this.companiesWithDiscrepancies.length === 0) {
      alert('No companies with discrepancies queued. Please run the Batch Scan first.');
      return;
    }

    const count = this.companiesWithDiscrepancies.length;
    const confirmed = confirm(
      `⚡ RUN MIGRATION LOOP FOR ${count} COMPANIES WITH DISCREPANCIES?\n\n` +
      `- Each company will be migrated one by one in a safe chronological loop.\n` +
      `- Existing paid amounts (amountPaid) are 100% LOCKED and will NOT change.\n` +
      `- Ledger fields (beginningBalance, totalFee, totalDue, remainingBalance) will be recalculated.\n\n` +
      `Proceed with the batch migration loop?`
    );
    if (!confirmed) return;

    this.isBatchMigrating = true;
    this.batchProgressPercent = 0;
    this.ledgerMigrationLogs.push(`Starting Migration Loop for ${count} companies...`);

    try {
      const storesSnap = await getDocs(collection(this.firestore, 'stores'));
      const allStores: any[] = storesSnap.docs.map(d => ({ ...d.data(), id: d.id }));

      const ratesSnap = await getDocs(collection(this.firestore, 'smrs_rates'));
      const allRates = ratesSnap.docs.map(d => ({ ...d.data(), id: d.id } as SmrsRate));

      let successCount = 0;

      for (let i = 0; i < this.companiesWithDiscrepancies.length; i++) {
        const target = this.companiesWithDiscrepancies[i];
        this.batchProgressPercent = Math.round(((i + 1) / count) * 100);
        this.batchProgressText = `Migrating (${i + 1}/${count}): ${target.companyName}...`;
        this.ledgerMigrationLogs.push(`[${i + 1}/${count}] Recalculating ledger forward for ${target.companyName} from ${target.earliestPeriod}...`);

        try {
          await this.invoiceService.recalculateLedgerForward(target.companyId, target.earliestPeriod, allStores, allRates);
          successCount++;
          this.ledgerMigrationLogs.push(`✓ [${i + 1}/${count}] Completed ${target.companyName}.`);
        } catch (compErr: any) {
          console.error(`Failed to migrate company ${target.companyName}:`, compErr);
          this.ledgerMigrationLogs.push(`❌ Error migrating ${target.companyName}: ${compErr.message || compErr}`);
        }
      }

      this.ledgerMigrationLogs.push(`Migration Loop Complete! Successfully migrated ${successCount} out of ${count} companies.`);
      alert(`Batch migration completed successfully!\n\nMigrated: ${successCount} / ${count} companies.`);

      // Clear discrepancy queue
      this.companiesWithDiscrepancies = [];

      // If a specific company was selected, refresh its view
      if (this.ledgerMigrationCompanyId) {
        await this.previewInvoiceLedgerRecalculation();
      }
    } catch (loopErr: any) {
      console.error('Error during migration loop:', loopErr);
      this.ledgerMigrationLogs.push(`Loop error: ${loopErr.message || loopErr}`);
      alert(`Error during migration loop: ${loopErr.message || loopErr}`);
    } finally {
      this.isBatchMigrating = false;
    }
  }

  // ==========================================
  // Master Data Export / Import Methods
  // ==========================================

  async exportMasterData() {
    this.isExportingMasterData = true;
    this.masterDataLogs = ['Starting Master Data Export...'];

    try {
      this.masterDataLogs.push('Fetching companies (collection: "company")...');
      const compSnap = await getDocs(collection(this.firestore, 'company'));
      const companies = compSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      this.masterDataLogs.push(`✓ Fetched ${companies.length} companies.`);

      this.masterDataLogs.push('Fetching stores (collection: "stores")...');
      const storeSnap = await getDocs(collection(this.firestore, 'stores'));
      const stores = storeSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      this.masterDataLogs.push(`✓ Fetched ${stores.length} stores.`);

      this.masterDataLogs.push('Fetching devices (collection: "devices")...');
      const devSnap = await getDocs(collection(this.firestore, 'devices'));
      const devices = devSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      this.masterDataLogs.push(`✓ Fetched ${devices.length} devices.`);

      this.masterDataLogs.push('Fetching company ownerships (collection: "company_ownerships")...');
      const ownRelSnap = await getDocs(collection(this.firestore, 'company_ownerships'));
      const companyOwnerships = ownRelSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      this.masterDataLogs.push(`✓ Fetched ${companyOwnerships.length} company ownership records.`);

      this.masterDataLogs.push('Fetching owners (collection: "owners")...');
      const ownerSnap = await getDocs(collection(this.firestore, 'owners'));
      const owners = ownerSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      this.masterDataLogs.push(`✓ Fetched ${owners.length} owners.`);

      this.masterDataLogs.push('Fetching brands (collection: "brands")...');
      const brandSnap = await getDocs(collection(this.firestore, 'brands'));
      const brands = brandSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      this.masterDataLogs.push(`✓ Fetched ${brands.length} brands.`);

      const exportPayload = {
        version: 1,
        exportedAt: new Date().toISOString(),
        metadata: {
          totalCompanies: companies.length,
          totalStores: stores.length,
          totalDevices: devices.length,
          totalOwnerships: companyOwnerships.length,
          totalOwners: owners.length,
          totalBrands: brands.length
        },
        data: {
          companies,
          stores,
          devices,
          companyOwnerships,
          owners,
          brands
        }
      };

      const jsonStr = JSON.stringify(exportPayload, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const dateStr = new Date().toISOString().replace(/[:.]/g, '-');
      a.href = url;
      a.download = `kceg-master-data-${dateStr}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      const totalCount = companies.length + stores.length + devices.length + companyOwnerships.length + owners.length + brands.length;
      this.masterDataLogs.push(`Master Data Export complete! Downloaded ${totalCount} total records across 6 collections.`);
    } catch (err: any) {
      console.error('Error exporting master data:', err);
      this.masterDataLogs.push(`Export error: ${err.message || err}`);
      alert(`Failed to export master data: ${err.message || err}`);
    } finally {
      this.isExportingMasterData = false;
    }
  }

  onMasterDataFileSelected(event: any) {
    const file = event.target?.files?.[0];
    if (!file) return;

    this.masterDataImportFileName = file.name;
    this.masterDataLogs = [`Selected file: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`];

    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const parsed = JSON.parse(e.target.result);
        if (!parsed.data || !Array.isArray(parsed.data.companies) || !Array.isArray(parsed.data.stores) || !Array.isArray(parsed.data.devices)) {
          throw new Error('Invalid master data format. File must contain "data" object with companies, stores, and devices arrays.');
        }

        this.masterDataImportData = parsed.data;
        this.masterDataImportStats = {
          companies: parsed.data.companies?.length || 0,
          stores: parsed.data.stores?.length || 0,
          devices: parsed.data.devices?.length || 0,
          companyOwnerships: parsed.data.companyOwnerships?.length || 0,
          owners: parsed.data.owners?.length || 0,
          brands: parsed.data.brands?.length || 0
        };

        const total = Object.values(this.masterDataImportStats).reduce((a, b) => a + b, 0);
        this.masterDataLogs.push(`File verified successfully. Ready to import ${total} records.`);
      } catch (err: any) {
        console.error('Error parsing master data file:', err);
        this.masterDataImportData = null;
        this.masterDataImportStats = null;
        this.masterDataLogs.push(`File error: ${err.message}`);
        alert(`Invalid master data file: ${err.message}`);
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  }

  cancelMasterDataImport() {
    this.masterDataImportData = null;
    this.masterDataImportStats = null;
    this.masterDataImportFileName = '';
    this.masterDataLogs.push('Import cancelled by user.');
  }

  private restoreFirestoreTimestamps(obj: any): any {
    if (obj === null || obj === undefined) return obj;
    if (typeof obj !== 'object') return obj;

    if (typeof obj.seconds === 'number' && typeof obj.nanoseconds === 'number' && Object.keys(obj).length <= 2) {
      return new Timestamp(obj.seconds, obj.nanoseconds);
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.restoreFirestoreTimestamps(item));
    }

    const result: any = {};
    for (const key of Object.keys(obj)) {
      result[key] = this.restoreFirestoreTimestamps(obj[key]);
    }
    return result;
  }

  private async clearCollection(colName: string): Promise<number> {
    this.masterDataLogs.push(`Scanning collection '${colName}' for existing records to purge...`);
    const colRef = collection(this.firestore, colName);
    const snap = await getDocs(colRef);
    if (snap.empty) {
      this.masterDataLogs.push(`Collection '${colName}' is already empty.`);
      return 0;
    }

    const docs = snap.docs;
    let deletedCount = 0;
    let batch = writeBatch(this.firestore);
    let batchOpCount = 0;

    for (let i = 0; i < docs.length; i++) {
      batch.delete(docs[i].ref);
      batchOpCount++;
      deletedCount++;

      if (batchOpCount >= 450) {
        await batch.commit();
        this.masterDataLogs.push(`Deleted ${batchOpCount} existing docs from '${colName}'...`);
        batch = writeBatch(this.firestore);
        batchOpCount = 0;
      }
    }

    if (batchOpCount > 0) {
      await batch.commit();
      this.masterDataLogs.push(`Deleted remaining ${batchOpCount} docs from '${colName}'.`);
    }

    this.masterDataLogs.push(`✓ Purged all ${deletedCount} existing records from '${colName}'.`);
    return deletedCount;
  }

  async executeMasterDataImport() {
    if (!this.masterDataImportData) return;

    const stats = this.masterDataImportStats;
    const totalCount = stats ? Object.values(stats).reduce((a, b) => a + b, 0) : 0;
    const confirmed = confirm(
      `⚠️ WARNING: ALL existing records in 'stores' and 'company' collections will be DELETED before importing!\n\n` +
      `Target Environment Brands: Preserved (JSON brands will NOT be imported; brands are mapped by name)\n\n` +
      `Records in selected JSON to import:\n` +
      `- Owners: ${stats?.owners || 0}\n` +
      `- Companies: ${stats?.companies || 0}\n` +
      `- Stores: ${stats?.stores || 0}\n` +
      `- Devices: ${stats?.devices || 0}\n` +
      `- Company Ownerships: ${stats?.companyOwnerships || 0}\n\n` +
      `Do you want to delete existing companies & stores and proceed with import?`
    );
    if (!confirmed) return;

    this.isImportingMasterData = true;
    this.masterDataLogs.push('Starting Master Data Import execution with pre-purge...');

    try {
      // Step 1: Purge all existing stores and companies
      this.masterDataLogs.push('--- PHASE 1: PURGING EXISTING STORES & COMPANIES ---');
      const storesDeleted = await this.clearCollection('stores');
      const companiesDeleted = await this.clearCollection('company');
      this.masterDataLogs.push(`Phase 1 Complete: Purged ${storesDeleted} stores and ${companiesDeleted} companies from Firestore.`);

      // Step 2: Resolve existing brands in target database
      this.masterDataLogs.push('--- PHASE 2: RESOLVING EXISTING BRANDS IN TARGET ENVIRONMENT ---');
      const existingBrandsSnap = await getDocs(collection(this.firestore, 'brands'));
      const existingBrands = existingBrandsSnap.docs.map(d => ({ id: d.id, ...d.data() } as any));
      this.masterDataLogs.push(`Found ${existingBrands.length} existing brand(s) in Firestore.`);

      // Build JSON brand ID -> Brand Name map from the imported JSON
      const jsonBrands: any[] = this.masterDataImportData.brands || [];
      const jsonBrandIdToName = new Map<string, string>();
      jsonBrands.forEach(b => {
        if (b.id && b.name) {
          jsonBrandIdToName.set(b.id, b.name);
        }
      });

      // Match name or source ID to existing brand document
      const resolveExistingBrandId = (brandVal: any): string => {
        if (!brandVal) return '';
        const rawStr = String(brandVal).trim();

        // 1. If rawStr already matches an existing brand ID, return it immediately
        const existingById = existingBrands.find(eb => eb.id === rawStr);
        if (existingById) {
          return existingById.id;
        }

        // 2. Normalize helper
        const norm = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

        // 3. Check if rawStr is mapped in JSON brands
        const brandName = jsonBrandIdToName.get(rawStr) || rawStr;
        const targetNorm = norm(brandName);

        // 4. Identify if this represents K-Photo Lavie or LFC
        const isTargetPhotoLavie = rawStr === 'T0RdUUPStz2b979i3K4A' || targetNorm.includes('photolavie') || targetNorm.includes('lavie') || targetNorm === 'kceg';
        const isTargetLfc = targetNorm === 'lfc' || targetNorm === 'lk' || targetNorm.includes('fourcuts') || targetNorm.includes('4cuts');

        // 5. Match against existing brands in Firestore
        for (const eb of existingBrands) {
          const ebNorm = norm(eb.name);
          if (ebNorm === targetNorm) {
            return eb.id;
          }
          if (isTargetPhotoLavie && (ebNorm.includes('photolavie') || ebNorm.includes('lavie'))) {
            return eb.id;
          }
          if (isTargetLfc && (ebNorm === 'lfc' || ebNorm === 'lk' || ebNorm.includes('fourcuts') || ebNorm.includes('4cuts'))) {
            return eb.id;
          }
        }

        return rawStr;
      };

      // Step 3: Import data from JSON (SKIPPING brands collection)
      this.masterDataLogs.push('--- PHASE 3: IMPORTING MASTER DATA (BRANDS EXCLUDED) ---');
      const collectionsToImport: { name: string; key: string }[] = [
        { name: 'owners', key: 'owners' },
        { name: 'company', key: 'companies' },
        { name: 'stores', key: 'stores' },
        { name: 'devices', key: 'devices' },
        { name: 'company_ownerships', key: 'companyOwnerships' }
      ];

      let grandTotalWritten = 0;

      for (const colInfo of collectionsToImport) {
        const rawDocs: any[] = this.masterDataImportData[colInfo.key] || [];
        if (rawDocs.length === 0) continue;

        this.masterDataLogs.push(`Importing ${rawDocs.length} records into '${colInfo.name}'...`);

        let batch = writeBatch(this.firestore);
        let batchOpCount = 0;

        for (let i = 0; i < rawDocs.length; i++) {
          const item = rawDocs[i];
          const docId = String(item.id || item.deviceId || item.storeId || '').trim();
          if (!docId) {
            this.masterDataLogs.push(`Skipping document in ${colInfo.name} without valid ID.`);
            continue;
          }

          const { id, ...dataWithoutId } = item;
          const cleanedData = this.restoreFirestoreTimestamps(dataWithoutId);

          // Map brand and brandId fields to existing brand IDs in target database
          if (cleanedData['brand']) {
            cleanedData['brand'] = resolveExistingBrandId(cleanedData['brand']);
          }
          if (cleanedData['brandId']) {
            cleanedData['brandId'] = resolveExistingBrandId(cleanedData['brandId']);
          }

          const docRef = doc(this.firestore, colInfo.name, docId);

          batch.set(docRef, cleanedData, { merge: true });
          batchOpCount++;
          grandTotalWritten++;

          if (batchOpCount >= 450) {
            await batch.commit();
            this.masterDataLogs.push(`Committed chunk of ${batchOpCount} docs in '${colInfo.name}'...`);
            batch = writeBatch(this.firestore);
            batchOpCount = 0;
          }
        }

        if (batchOpCount > 0) {
          await batch.commit();
          this.masterDataLogs.push(`Committed remaining ${batchOpCount} docs in '${colInfo.name}'.`);
        }
      }

      this.masterDataLogs.push(`Master Data Import complete! Deleted ${storesDeleted} old stores and ${companiesDeleted} old companies. Successfully imported ${grandTotalWritten} records mapped to existing brands.`);
      alert(`Master Data Import completed successfully!\n\nDeleted: ${storesDeleted} stores, ${companiesDeleted} companies\nImported: ${grandTotalWritten} total records\nBrands: Preserved existing environment brands.`);

      this.masterDataImportData = null;
      this.masterDataImportStats = null;
      this.masterDataImportFileName = '';

      // Refresh companies in local DevTool state
      const compSnap = await getDocs(collection(this.firestore, 'company'));
      this.companies = compSnap.docs.map(d => ({ ...d.data(), id: d.id }));
    } catch (err: any) {
      console.error('Error importing master data:', err);
      this.masterDataLogs.push(`Import execution error: ${err.message || err}`);
      alert(`Error during master data import: ${err.message || err}`);
    } finally {
      this.isImportingMasterData = false;
    }
  }

  // ---- Coupon brand migration (KAN-230) ----
  couponMigrationProjectId = '';
  couponMigrationBrand: LavieBrandResolution | null = null;
  couponMigrationPlan: CouponMigrationPlan | null = null;
  couponMigrationOutcome: CouponMigrationOutcome | null = null;
  couponMigrationTypedProject = '';
  couponMigrationLogs: string[] = [];
  isScanningCoupons = false;
  isApplyingCouponMigration = false;
  couponMigrationProgress = '';

  get couponMigrationConfirmed(): boolean {
    return isMigrationConfirmed(this.couponMigrationTypedProject, this.couponMigrationProjectId, this.couponMigrationPlan)
      && this.couponMigrationBrand?.status === 'ok';
  }

  get couponMigrationStatusRows(): { status: string; count: number }[] {
    const by = this.couponMigrationPlan?.byStatus || {};
    return Object.keys(by).sort().map(status => ({ status, count: by[status] }));
  }

  /** Read-only: shows the environment, resolves the target brand and lists the before/after scope of every coupon. */
  async previewCouponBrandMigration() {
    this.isScanningCoupons = true;
    this.couponMigrationPlan = null;
    this.couponMigrationOutcome = null;
    this.couponMigrationTypedProject = '';
    this.couponMigrationLogs = [];
    try {
      this.couponMigrationProjectId = (this.firestore as any)?.app?.options?.projectId || '';
      this.couponMigrationLogs.push(`Environment (Firebase project): ${this.couponMigrationProjectId || 'unknown'}`);

      const brandSnap = await getDocs(collection(this.firestore, 'brands'));
      this.couponMigrationBrand = resolveCanonicalLavieBrand(brandSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) })));
      if (this.couponMigrationBrand.status !== 'ok') {
        this.couponMigrationLogs.push(`BLOCKED: ${this.couponMigrationBrand.message}`);
        return;
      }
      this.couponMigrationLogs.push(`Target brand: ${this.couponMigrationBrand.brandName} (${this.couponMigrationBrand.brandId})`);

      const snap = await getDocs(collection(this.firestore, 'coupons'));
      this.couponMigrationPlan = planCouponBrandMigration(
        snap.docs.map(d => ({ id: d.id, data: d.data() as Record<string, any> })),
        this.couponMigrationBrand.brandId
      );
      const p = this.couponMigrationPlan;
      this.couponMigrationLogs.push(`Scanned ${p.total} coupons (${p.softDeleted} soft-deleted): ${p.toUpdate} to update, ${p.toSkip} already correct. Nothing was written.`);
    } catch (err: any) {
      console.error('Coupon migration preview failed', err);
      this.couponMigrationLogs.push(`Preview failed: ${err?.message || err}`);
    } finally {
      this.isScanningCoupons = false;
    }
  }

  /** Writes only `applicableBrands`, in bounded batches, after downloading the pre-migration snapshot. */
  async executeCouponBrandMigration() {
    const plan = this.couponMigrationPlan;
    const brand = this.couponMigrationBrand;
    if (!plan || !brand || brand.status !== 'ok' || !this.couponMigrationConfirmed || this.isApplyingCouponMigration) return;
    if (!confirm(`Set applicableBrands to "${brand.brandName}" on ${plan.toUpdate} coupons in project ${this.couponMigrationProjectId}?`)) return;

    this.isApplyingCouponMigration = true;
    this.couponMigrationOutcome = null;
    try {
      // Rollback comparison: every coupon's scope before the change.
      this.downloadJson(`coupon-brand-snapshot_${this.couponMigrationProjectId}_${new Date().toISOString().replace(/[:.]/g, '-')}.json`, {
        projectId: this.couponMigrationProjectId,
        targetBrandId: brand.brandId,
        createdAt: new Date().toISOString(),
        coupons: plan.rows.map(r => ({ id: r.id, applicableBrands: r.before, status: r.status, isDeleted: r.isDeleted }))
      });
      this.couponMigrationLogs.push('Pre-migration snapshot downloaded.');

      this.couponMigrationOutcome = await applyCouponBrandMigration(
        plan,
        async ids => {
          const batch = writeBatch(this.firestore);
          ids.forEach(id => batch.update(doc(this.firestore, 'coupons', id), { applicableBrands: [brand.brandId] }));
          await batch.commit();
        },
        400,
        (done, total) => (this.couponMigrationProgress = `${done} / ${total}`)
      );
      const o = this.couponMigrationOutcome;
      this.couponMigrationLogs.push(`Updated ${o.updated.length}, skipped ${o.skipped.length}, failed ${o.failed.length}.`);
      this.couponMigrationLogs.push(o.failed.length === 0
        ? 'Migration complete. Run the preview again to confirm 0 coupons remain.'
        : `PARTIAL: ${o.failed.length} coupons were not updated. Run the preview again and apply to retry only those.`);
    } catch (err: any) {
      console.error('Coupon migration failed', err);
      this.couponMigrationLogs.push(`Migration stopped: ${err?.message || err}`);
    } finally {
      this.isApplyingCouponMigration = false;
      this.couponMigrationProgress = '';
    }
  }

  private downloadJson(fileName: string, payload: unknown) {
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }
}
